﻿/*
  HN Cart Drawer interactions.

  Currently only handles bundle removal: clicking the single Remove
  button on a grouped bundle card zeros every line item in the
  bundle in one /cart/update.js round-trip, then swaps the returned
  section HTML into the DOM. Going through update.js (rather than
  N x /cart/change.js calls) keeps it to one network request and
  one re-render even for 3-pack bundles.

  Listener is delegated on document so it survives the drawer being
  re-rendered (Shopify's cart-drawer-items replaces its own inner
  HTML on every cart change, which detaches any element-level
  listeners we'd attach directly).
*/

(function () {
  'use strict';

  /* Re-init guard. This script is loaded from BOTH
     snippets/cart-drawer.liquid AND sections/main-cart-items.liquid
     (both render on /cart). Without this guard the IIFE runs
     twice, registering two delegated click listeners â€” every
     click then fires both, so the stepper increments by 2,
     bundle remove buttons double-fire, etc. */
  if (window.__hnCartDrawerInit__) return;
  window.__hnCartDrawerInit__ = true;

  /* Sections we want the server to return rendered HTML for after
     the update. cart-drawer = the drawer body, cart-icon-bubble =
     header cart count. main-cart-items / main-cart-footer are the
     /cart page columns â€” included unconditionally so the same
     update payload reconciles BOTH surfaces (the drawer state on
     mini-cart pages AND the page body when the user is on /cart).
     Shopify just returns empty strings for sections whose
     templates don't exist on the current route, so over-requesting
     is cheap. */
  var REFRESH_SECTIONS = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];
  var MAX_PRODUCT_QTY = 30;
  var BUNDLE_RECONCILE_DEBOUNCE_MS = 250;
  var BUNDLE_RECONCILE_MAX_ITERATIONS = 12;
  var bundleReconcileTimer = null;
  var bundleReconcileInFlight = false;
  var bundleReconcilePromise = null;
  var bundleReconcileMutationGen = 0;
  var bundleReconcileFromSelf = false;
  var bundleReconcilePoison = '';
  var bundleReconcileMissingAllocationWarnings = {};
  var cartMutationPromises = [];
  var checkoutSubmitUnlocked = false;
  var checkoutFlushInProgress = false;

  function cartQtyScope(member) {
    return (member && member.closest('cart-drawer, cart-items')) || document;
  }

  function productTotalQty(member) {
    if (!member) return 0;
    var productId = member.getAttribute('data-hn-product-id');
    if (!productId) {
      return parseInt(member.getAttribute('data-hn-variant-qty'), 10) || 0;
    }

    var total = 0;
    cartQtyScope(member).querySelectorAll('[data-hn-variant-keys][data-hn-product-id="' + productId + '"]').forEach(function (el) {
      total += parseInt(el.getAttribute('data-hn-variant-qty'), 10) || 0;
    });
    return total;
  }

  function memberMaxQty(member) {
    var max = MAX_PRODUCT_QTY;
    if (!member) return max;
    var currentQty = parseInt(member.getAttribute('data-hn-variant-qty'), 10) || 0;
    var productMaxForMember = MAX_PRODUCT_QTY - productTotalQty(member) + currentQty;
    if (productMaxForMember < max) max = productMaxForMember;

    var ruleMax = parseInt(member.getAttribute('data-hn-quantity-rule-max'), 10);
    if (!isNaN(ruleMax) && ruleMax >= 0 && ruleMax < max) max = ruleMax;

    var management = member.getAttribute('data-hn-inventory-management');
    var policy = member.getAttribute('data-hn-inventory-policy');
    if (management === 'shopify' && policy !== 'continue') {
      var stock = parseInt(member.getAttribute('data-hn-inventory-quantity'), 10);
      if (!isNaN(stock) && stock < max) max = stock;
    }

    return Math.max(0, max);
  }

  function syncMemberQtyButtons(member) {
    if (!member) return;
    var qty = parseInt(member.getAttribute('data-hn-variant-qty'), 10) || 0;
    var max = memberMaxQty(member);
    member.querySelectorAll('[data-hn-chip-step="dec"], [data-hn-bundle-variant-qty="decrement"]').forEach(function (btn) {
      btn.disabled = qty <= 0;
      btn.setAttribute('aria-disabled', qty <= 0 ? 'true' : 'false');
    });
    member.querySelectorAll('[data-hn-chip-step="inc"], [data-hn-bundle-variant-qty="increment"]').forEach(function (btn) {
      btn.disabled = qty >= max;
      btn.setAttribute('aria-disabled', qty >= max ? 'true' : 'false');
    });
  }

  function syncProductQtyButtons(member) {
    if (!member) return;
    var productId = member.getAttribute('data-hn-product-id');
    if (!productId) {
      syncMemberQtyButtons(member);
      return;
    }
    cartQtyScope(member).querySelectorAll('[data-hn-variant-keys][data-hn-product-id="' + productId + '"]').forEach(syncMemberQtyButtons);
  }

  function syncAllCartQtyControls(root) {
    (root || document).querySelectorAll('[data-hn-variant-keys]').forEach(syncMemberQtyButtons);
  }

  function setDiscountFormError(form, message) {
    var input = form && form.querySelector('[data-hn-cart-discount-input]');
    var messageEl = form && form.querySelector('[data-hn-cart-discount-message]');
    if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
    if (messageEl) {
      messageEl.textContent = message || '';
      messageEl.hidden = !message;
    }
  }

  function setDiscountFormLoading(form, loading) {
    var input = form && form.querySelector('[data-hn-cart-discount-input]');
    var button = form && form.querySelector('[data-hn-cart-discount-apply]');
    if (input) input.disabled = loading;
    if (button) {
      button.disabled = loading;
      button.textContent = loading ? 'Applying' : 'Apply';
    }
  }

  function normalizeDiscountCode(value) {
    return String(value || '').trim().toLowerCase();
  }

  function discountTitleMatches(value, code) {
    return normalizeDiscountCode(value) === normalizeDiscountCode(code);
  }

  function formatDiscountCodeForMessage(code) {
    return String(code || '').trim().toUpperCase();
  }

  function isKnownNonStackingDiscountCode(code) {
    return normalizeDiscountCode(code) === 'getlucky';
  }

  function discountConflictMessage(code) {
    return formatDiscountCodeForMessage(code) + " can't be combined with your current discount. We kept the better deal for you.";
  }

  function discountGenericMessage(hasCartLevelDiscount) {
    if (hasCartLevelDiscount) {
      return "That code couldn't be applied. It may be invalid, expired, or unable to combine with your current discount.";
    }
    return "That code couldn't be applied. Check the code and try again.";
  }

  function cartDataHasCartLevelDiscount(cartData) {
    return !!(cartData && cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length);
  }

  function cartDataHasCartLevelDiscountCode(cartData, code) {
    if (!cartData || !code) return false;
    var cartApplications = cartData.cart_level_discount_applications || [];
    for (var i = 0; i < cartApplications.length; i += 1) {
      var cartApplication = cartApplications[i];
      if (cartApplication && discountTitleMatches(cartApplication.title || cartApplication.code, code)) return true;
    }
    return false;
  }

  function renderedCartHasCartLevelDiscount() {
    return !!document.querySelector('[data-hn-cart-level-discount]');
  }

  function cartDataHasDiscountCode(cartData, code) {
    if (!cartData || !code) return false;

    var discountCodes = cartData.discount_codes || [];
    for (var i = 0; i < discountCodes.length; i += 1) {
      var discountCode = discountCodes[i];
      if (!discountCode || !discountTitleMatches(discountCode.code || discountCode.title, code)) continue;
      if (Object.prototype.hasOwnProperty.call(discountCode, 'applicable') && !discountCode.applicable) continue;
      return true;
    }

    var cartApplications = cartData.cart_level_discount_applications || [];
    for (var j = 0; j < cartApplications.length; j += 1) {
      var cartApplication = cartApplications[j];
      if (cartApplication && discountTitleMatches(cartApplication.title || cartApplication.code, code)) return true;
    }

    var items = cartData.items || [];
    for (var k = 0; k < items.length; k += 1) {
      var item = items[k] || {};
      var discounts = item.discounts || item.line_level_discount_allocations || [];
      for (var d = 0; d < discounts.length; d += 1) {
        var discount = discounts[d] || {};
        var app = discount.discount_application || {};
        if (discountTitleMatches(discount.title || discount.code || app.title || app.code, code)) return true;
      }

      var allocations = item.discount_allocations || [];
      for (var a = 0; a < allocations.length; a += 1) {
        var allocationApp = (allocations[a] && allocations[a].discount_application) || {};
        if (discountTitleMatches(allocationApp.title || allocationApp.code, code)) return true;
      }
    }

    return false;
  }

  function trackCartMutationPromise(promise) {
    if (!promise || typeof promise.then !== 'function') return promise;
    cartMutationPromises.push(promise);
    promise.finally(function () {
      var index = cartMutationPromises.indexOf(promise);
      if (index !== -1) cartMutationPromises.splice(index, 1);
    });
    return promise;
  }

  function waitForCartMutations() {
    if (!cartMutationPromises.length) return Promise.resolve();
    return Promise.all(cartMutationPromises.slice());
  }

  document.addEventListener('click', function (e) {
    /* Any pending qty changes on rows OTHER than the one being
       clicked get committed first. Lets the user mash +/- inside
       a row without auto-saving mid-interaction, but the moment
       they click anywhere else (another row, the close button,
       the page background, Checkout) the change settles. Runs
       before specific click handlers so the post-commit section
       swap doesn't race with whatever the click triggers next. */
    commitPendingQtyIfOutside(e.target);

    var discountToggle = e.target.closest('[data-hn-cart-discount-toggle]');
    if (discountToggle) {
      e.preventDefault();
      var discountWrap = discountToggle.closest('[data-hn-cart-discount]');
      var discountForm = discountWrap && discountWrap.querySelector('[data-hn-cart-discount-form]');
      var discountInput = discountForm && discountForm.querySelector('[data-hn-cart-discount-input]');
      if (!discountForm) return;

      discountWrap.classList.add('is-open');
      discountToggle.setAttribute('aria-expanded', 'true');
      setDiscountFormError(discountForm, '');
      if (discountInput) discountInput.focus();
      return;
    }

    /* Per-variant remove: drops every line item for THIS variant
       only, leaving other bundle variants intact. The button is
       used by single-variant bundle ROWS where data-hn-variant-keys
       lives on the row (.cart-item--bundle-member). Closest
       ancestor with the data attr is the row. */
    var removeBtn = e.target.closest('[data-hn-bundle-variant-remove]');
    if (removeBtn) {
      e.preventDefault();
      var member = removeBtn.closest('[data-hn-variant-keys]');
      if (!member || member.classList.contains('is-loading')) return;
      var keys = parseVariantKeys(member);
      if (!keys.length) return;

      member.classList.add('is-loading', 'is-removing');
      setMemberBusy(member, true);
      /* Mirror the PDP/upsell ATC loading state: text fades to
         transparent, spinner appears in the button. Matched CSS
         lives on .cart-remove-button.loading + .hn-cart-page__
         remove.loading. */
      setRemoveButtonLoading(removeBtn, true);

      /* Optimistic remove â€” collapse the row + decrement count
         badge + shipbar BEFORE the network round-trip so the cart
         responds instantly. Server reconciles via section swap. */
      var removeOptimistic = applyRemoveOptimistic(member);

      var updates = buildConsolidatedUpdates(keys, 0);
      postUpdates(updates, function onError() {
        revertRemoveOptimistic(removeOptimistic);
        member.classList.remove('is-loading', 'is-removing');
        setMemberBusy(member, false);
        setRemoveButtonLoading(removeBtn, false);
      }, function onSuccess() {
        /* Recorded on confirmation, not at click time: a failed POST
           reverts the UI and must leave no phantom remove event. */
        recordCartLineEvent('hi_cart_item_remove', keys, 0, removeOptimistic.qty);
      });
      return;
    }

    /* Bundle-level remove: drops EVERY line item of the bundle
       product in a single update.js call. Used by the multi-variant
       combined row's "Remove bundle" button. Aggregates variant
       keys from each chip inside the row. */
    var bundleRemoveBtn = e.target.closest('[data-hn-bundle-remove]');
    if (bundleRemoveBtn) {
      e.preventDefault();
      var bundleRow = bundleRemoveBtn.closest('[data-hn-bundle-product-id]');
      if (!bundleRow || bundleRow.classList.contains('is-loading')) return;
      var chipKeys = [];
      bundleRow.querySelectorAll('[data-hn-variant-keys]').forEach(function (chip) {
        chipKeys = chipKeys.concat(parseVariantKeys(chip));
      });
      if (!chipKeys.length) return;

      bundleRow.classList.add('is-loading', 'is-removing');
      bundleRemoveBtn.disabled = true;
      setRemoveButtonLoading(bundleRemoveBtn, true);

      /* Optimistic bundle-level remove. */
      var bundleRemoveOptimistic = applyRemoveOptimistic(bundleRow);

      var bundleUpdates = {};
      chipKeys.forEach(function (k) { bundleUpdates[k] = 0; });
      postUpdates(bundleUpdates, function onError() {
        revertRemoveOptimistic(bundleRemoveOptimistic);
        bundleRow.classList.remove('is-loading', 'is-removing');
        bundleRemoveBtn.disabled = false;
        setRemoveButtonLoading(bundleRemoveBtn, false);
      }, function onSuccess() {
        recordCartLineEvent('hi_cart_item_remove', chipKeys, 0, bundleRemoveOptimistic.qty);
      });
      return;
    }

    /* Per-variant stepper. Same logic for single-variant bundle
       rows (closest is the row) and multi-variant bundle chips
       (closest is the chip). data-hn-variant-keys + data-hn-
       variant-qty are read off the closest matching ancestor. */
    var qtyBtn = e.target.closest('[data-hn-bundle-variant-qty], [data-hn-chip-step]');
    if (qtyBtn) {
      e.preventDefault();
      var stepMember = qtyBtn.closest('[data-hn-variant-keys]');
      if (!stepMember) return;
      var stepKeys = parseVariantKeys(stepMember);
      if (!stepKeys.length) return;

      var currentQty = parseInt(stepMember.getAttribute('data-hn-variant-qty'), 10) || 1;
      var direction = qtyBtn.getAttribute('data-hn-bundle-variant-qty') || qtyBtn.getAttribute('data-hn-chip-step');
      var inc = direction === 'increment' || direction === 'inc';
      var maxQty = memberMaxQty(stepMember);
      if (inc && currentQty >= maxQty) {
        syncProductQtyButtons(stepMember);
        return;
      }
      var newQty = inc ? currentQty + 1 : currentQty - 1;
      if (newQty < 0) newQty = 0;
      if (newQty > maxQty) newQty = maxQty;
      if (newQty === currentQty) return;

      /* Apply optimistic UI on EVERY click â€” qty, chip text, line
         price, count badge, shipbar â€” so each tap responds on the
         click frame. No is-loading class while the user is still
         clicking (that would block subsequent taps). The actual
         server POST is debounced below; loading state kicks in
         only when the commit fires after the user pauses. */
      applyQtyChangeOptimistic(stepMember, currentQty, newQty);

      if (newQty === 0) {
        /* Drop-to-0 is a removal: commit immediately like the Remove
           button so the row clears in ~300ms instead of lingering
           faded until the user clicks outside. applyQtyChangeOptimistic
           already faded the right element (the chip for a multi-variant
           bundle, the whole row otherwise) and applied the removal's
           footer deltas; cancel any commit still pending from earlier
           steps, then fire the 0-qty POST now. */
        cancelPendingQtyCommit(stepMember);
        commitQtyChange(stepMember, stepKeys);
      } else {
        /* Debounced commit. Each click cancels the previous pending
           POST and reschedules ~350ms out. Rapid +/- mashing
           collapses into ONE final POST with the settled qty
           instead of N round-trips back-to-back. */
        scheduleQtyCommit(stepMember, stepKeys);
      }
      return;
    }

    /* Chip expand / collapse for multi-variant bundle chips.
       Click the collapsed face -> toggle .is-expanded on the chip
       which CSS uses to swap collapsed face for the inline
       stepper face. Click outside any expanded chip closes them
       all (handled below by a separate document listener). */
    var chipExpand = e.target.closest('[data-hn-chip-expand]');
    if (chipExpand) {
      e.preventDefault();
      var chip = chipExpand.closest('.hn-bundle-chip');
      if (!chip) return;
      // Close any other open chip on the same bundle row
      var bundleScope = chip.closest('[data-hn-bundle-product-id]') || document;
      bundleScope.querySelectorAll('.hn-bundle-chip.is-expanded').forEach(function (c) {
        if (c !== chip) c.classList.remove('is-expanded');
      });
      chip.classList.add('is-expanded');
      return;
    }
    var chipCollapse = e.target.closest('[data-hn-chip-collapse]');
    if (chipCollapse) {
      e.preventDefault();
      var collapseChip = chipCollapse.closest('.hn-bundle-chip');
      if (collapseChip) collapseChip.classList.remove('is-expanded');
      return;
    }

    /* Click outside any expanded chip closes them all. Runs only
       if no other chip-related handler matched above. The check
       for .hn-bundle-chip ancestor lets clicks inside an expanded
       chip's stepper buttons reach those handlers first without
       collapsing the chip mid-interaction. */
    if (!e.target.closest('.hn-bundle-chip')) {
      document.querySelectorAll('.hn-bundle-chip.is-expanded').forEach(function (c) {
        c.classList.remove('is-expanded');
      });
    }

    /* Upsell title / thumb click: fires select_promotion. Doesn't
       preventDefault â€” the anchor still navigates to the PDP. We
       only piggyback for analytics. Dedupe per card per session
       so multiple clicks don't spam GA4. */
    var upsellTrackLink = e.target.closest('[data-hn-upsell-track]');
    if (upsellTrackLink) {
      var trackedCard = upsellTrackLink.closest('[data-hn-upsell-card]');
      if (trackedCard) fireSelectPromotion(trackedCard);
      // Fall through â€” anchor still navigates.
    }

    /* Bundle-bump stepper +/-. Updates the card's
       data-hn-stepper-state attribute and toggles which qty-keyed
       eyebrow / price / peek spans are visible. The Add button
       reads the state when clicked to pass qty into the add call. */
    var stepperDec = e.target.closest('[data-hn-stepper-dec]');
    if (stepperDec) {
      e.preventDefault();
      if (stepperDec.getAttribute('aria-disabled') === 'true') return;
      var decCard = stepperDec.closest('[data-hn-upsell-card]');
      if (decCard) setUpsellStepperState(decCard, getUpsellStepperState(decCard) - 1);
      return;
    }
    var stepperInc = e.target.closest('[data-hn-stepper-inc]');
    if (stepperInc) {
      e.preventDefault();
      if (stepperInc.getAttribute('aria-disabled') === 'true') return;
      var incCard = stepperInc.closest('[data-hn-upsell-card]');
      if (incCard) setUpsellStepperState(incCard, getUpsellStepperState(incCard) + 1);
      return;
    }

    /* Upsell ATC: variant id is fixed per card (server picked the
       gap-closing or quantity-bump variant during render), so we
       just read data-hn-upsell-variant-id off the button. Applies
       PDP-grade optimistic UX (ghost row in drawer + cart-count
       badge bump + shipbar tween) BEFORE the network fires, so the
       add feels instant even on slow connections. Server response
       reconciles via section swap. */
    var upsellBtn = e.target.closest('[data-hn-upsell-add]');
    if (upsellBtn) {
      e.preventDefault();
      /* aria-disabled is the lock here (matching the PDP ATC
         pattern in product-form.js). Native `disabled` would also
         work but aria-disabled keeps the button focusable for
         keyboard users while still blocking activation. */
      if (upsellBtn.getAttribute('aria-disabled') === 'true') return;
      var upsellCard = upsellBtn.closest('[data-hn-upsell-card]');
      var variantId = upsellBtn.getAttribute('data-hn-upsell-variant-id');
      if (!variantId) return;

      /* Capture analytics payload BEFORE the section swap erases
         the source card. Closure keeps the card reference alive
         independently of DOM mutations. */
      var cardForAnalytics = upsellCard;

      /* Stepper qty (bundle-bump cards). Non-bundle cards have no
         stepper; getUpsellStepperState falls back to 1 for them. */
      var upsellQty = getUpsellStepperState(upsellCard);

      setUpsellButtonLoading(upsellBtn, true);

      /* Apply optimistic UI updates first so visible feedback
         happens on the click frame rather than after the network
         round-trip. Captured into closure for rollback on error. */
      var optimistic = applyUpsellOptimistic(upsellCard, upsellQty);

      /* Offer-time telemetry snapshot: must happen NOW, while the card
         is attached and the decision key still describes the pre-add
         cart state (the success callback runs after the section swap). */
      var telemetryAtClick = cardForAnalytics ? captureUpsellTelemetry(cardForAnalytics) : null;

      addUpsellToCart(variantId, upsellQty, function onDone(err) {
        if (err) {
          // Network failed - roll back the optimistic state so the
          // user sees the cart return to its pre-click condition,
          // and re-enable the button so they can retry.
          revertUpsellOptimistic(optimistic);
          setUpsellButtonLoading(upsellBtn, false);
          return;
        }
        // Success - fire GA4 add_to_cart event with the captured
        // card payload before the new section HTML obliterates it.
        if (cardForAnalytics) fireAddToCart(cardForAnalytics, upsellQty, telemetryAtClick);
        // On success the section swap re-renders fresh cards
        // (different products picked OR threshold now met and cards
        // disappear/transform) AND replaces the ghost row with the
        // canonical server row, so we don't need to restore the
        // button or clean up the ghost; the swap handles both.
      }, upsellCard);
      return;
    }
  });

  document.addEventListener('submit', function (e) {
    var discountForm = e.target.closest('[data-hn-cart-discount-form]');
    if (!discountForm) return;

    e.preventDefault();

    var input = discountForm.querySelector('[data-hn-cart-discount-input]');
    var code = input ? input.value.trim() : '';
    if (!code) {
      setDiscountFormError(discountForm, 'Enter a promo code.');
      if (input) input.focus();
      return;
    }

    var previousTotal = parseInt(discountForm.getAttribute('data-hn-cart-total'), 10);
    var previousDiscountTotal = parseInt(discountForm.getAttribute('data-hn-cart-discount-total'), 10);
    setDiscountFormError(discountForm, '');
    setDiscountFormLoading(discountForm, true);

    postDiscountCode(code, function (err, cartData) {
      if (err) {
        recordDiscountApply(code, false);
        setDiscountFormLoading(discountForm, false);
        setDiscountFormError(discountForm, discountGenericMessage(false));
        if (input) input.focus();
        return;
      }

      var nextTotal = parseInt(cartData && cartData.total_price, 10);
      var nextDiscountTotal = parseInt(cartData && cartData.total_discount, 10);
      var codeAppears = cartDataHasDiscountCode(cartData, code);
      var hasCartLevelDiscount = renderedCartHasCartLevelDiscount() || cartDataHasCartLevelDiscount(cartData);
      var submittedCodeIsCartLevelDiscount = cartDataHasCartLevelDiscountCode(cartData, code);
      var totalChanged = !isNaN(previousTotal) && !isNaN(nextTotal) && nextTotal < previousTotal;
      var discountIncreased = !isNaN(previousDiscountTotal) && !isNaN(nextDiscountTotal) && nextDiscountTotal > previousDiscountTotal;

      if (hasCartLevelDiscount && !submittedCodeIsCartLevelDiscount && !totalChanged && !discountIncreased) {
        recordDiscountApply(code, false);
        setDiscountFormLoading(discountForm, false);
        if (isKnownNonStackingDiscountCode(code)) {
          setDiscountFormError(discountForm, discountConflictMessage(code));
        } else {
          setDiscountFormError(discountForm, discountGenericMessage(true));
        }
        if (input) input.focus();
        return;
      }

      if (!codeAppears && !totalChanged && !discountIncreased) {
        recordDiscountApply(code, false);
        setDiscountFormLoading(discountForm, false);
        if (hasCartLevelDiscount && isKnownNonStackingDiscountCode(code)) {
          setDiscountFormError(discountForm, discountConflictMessage(code));
        } else {
          setDiscountFormError(discountForm, discountGenericMessage(hasCartLevelDiscount));
        }
        if (input) input.focus();
        return;
      }

      recordDiscountApply(code, true);
      applyRenderedSections(cartData.sections || {}, cartData);
      notifyCartUpdate(cartData);
    });
  });

  document.addEventListener('input', function (e) {
    if (!e.target || !e.target.matches('[data-hn-cart-discount-input]')) return;
    var discountForm = e.target.closest('[data-hn-cart-discount-form]');
    if (discountForm) setDiscountFormError(discountForm, '');
  });

  /* Toggle the upsell ATC button between idle and in-flight loading
     state. Matches the PDP product-form.js pattern exactly:
       - `loading` class fades the label to transparent (.hn-btn.loading
         selector in assets/hn-core.css)
       - `.loading__spinner` SVG (rendered into the button at Liquid
         time via the loading-spinner snippet) shows when its `hidden`
         class is removed
       - aria-disabled locks click + announces state to AT users;
         aria-busy signals to AT that the operation is in progress
     Same on/off shape so failures restore cleanly on retry. */
  function setUpsellButtonLoading(btn, isLoading) {
    if (!btn) return;
    var spinner = btn.querySelector('.loading__spinner');
    if (isLoading) {
      btn.classList.add('loading');
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.classList.remove('loading');
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-busy');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  /* Same loading-state toggle as the ATC buttons, applied to the
     cart row's Remove button so the customer gets immediate
     visual feedback that the remove is in flight. Mirrors the
     PDP/upsell pattern: `loading` class fades the text to
     transparent (via .cart-remove-button.loading +
     .hn-cart-page__remove.loading in CSS); the .loading__spinner
     SVG centers and shows. */
  function setRemoveButtonLoading(btn, isLoading) {
    if (!btn) return;
    var spinner = btn.querySelector('.loading__spinner');
    if (isLoading) {
      btn.classList.add('loading');
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.classList.remove('loading');
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-busy');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  /* ============================================================
     Upsell optimistic UI

     Reuses count-badge + shipbar helpers from window.HnPdpCart
     (assets/hn-pdp-cart-optimistic.js, loaded globally in
     theme.liquid). Mirrors the PDP ATC pattern: ghost row + count
     badge + shipbar tween, all applied on the click frame, then
     reconciled by the section swap when /cart/add.js returns.

     Surface routing:
       - Drawer is OPEN  â†’ drawer ghost row (HnPdpCart.insertGhostRow)
       - On /cart page   â†’ cart-page ghost <li> (local helper here)
       - Else            â†’ skip ghost (count + shipbar still bump)
     Drawer is NOT opened by the upsell ATC â€” the user is already
     looking at the cart context they clicked from; opening the
     drawer over the /cart page would obscure their view.
     ============================================================ */
  /* ============================================================
     Stepper helpers (bundle-bump upsell cards only)

     Bundle-bump cards pre-render one eyebrow / price line / peek
     line per qty step (1..max). The card carries
     data-hn-stepper-state (currently selected step, default 1)
     and data-hn-stepper-max (the cap from Liquid). The +/- buttons
     mutate the state attribute; we toggle visibility on the
     [data-hn-qty-eyebrow|price|peek] children by matching their
     step attr against the new state. The Add button reads the
     state to pass qty into the add call.
     ============================================================ */
  function getUpsellStepperState(card) {
    if (!card) return 1;
    var v = parseInt(card.getAttribute('data-hn-stepper-state') || '1', 10);
    return v > 0 ? v : 1;
  }

  function setUpsellStepperState(card, newState) {
    if (!card) return;
    var max = parseInt(card.getAttribute('data-hn-stepper-max') || '1', 10);
    if (max < 1) max = 1;
    if (newState < 1) newState = 1;
    if (newState > max) newState = max;
    card.setAttribute('data-hn-stepper-state', String(newState));

    var valueEl = card.querySelector('[data-hn-stepper-value]');
    if (valueEl) valueEl.textContent = String(newState);

    ['eyebrow', 'price', 'sticker'].forEach(function (kind) {
      var attr = 'data-hn-qty-' + kind;
      card.querySelectorAll('[' + attr + ']').forEach(function (el) {
        var stepFor = parseInt(el.getAttribute(attr), 10);
        el.hidden = (stepFor !== newState);
      });
    });

    var dec = card.querySelector('[data-hn-stepper-dec]');
    var inc = card.querySelector('[data-hn-stepper-inc]');
    if (dec) dec.setAttribute('aria-disabled', newState <= 1 ? 'true' : 'false');
    if (inc) inc.setAttribute('aria-disabled', newState >= max ? 'true' : 'false');
  }

  function applyUpsellOptimistic(card, quantity) {
    quantity = parseInt(quantity, 10);
    if (!quantity || quantity < 1) quantity = 1;

    var state = {
      drawerGhost: null,
      pageGhost: null,
      countDelta: 0,
      shipDelta: 0,
      pageHeadingDelta: 0
    };
    if (!card) return state;

    var variantId = card.getAttribute('data-hn-upsell-variant-id');
    var priceCents = parseInt(card.getAttribute('data-hn-upsell-price-cents'), 10) || 0;
    var compareAtCents = parseInt(card.getAttribute('data-hn-upsell-compare-at-cents'), 10) || 0;
    if (!variantId || !priceCents) return state;

    /* Image selector covers both the current upsell card markup
       (.hn-cart-upsell-card__img) and any legacy ghost markup that
       may have used .hn-cart-upsell-card__thumb img. Falls through
       to whichever matches first. */
    var thumbImg = card.querySelector('.hn-cart-upsell-card__img, .hn-cart-upsell-card__thumb img');
    var productHandle = card.getAttribute('data-hn-upsell-product-handle') || '';
    var productTitle = card.getAttribute('data-hn-upsell-product-title') || '';
    var variantTitle = card.getAttribute('data-hn-upsell-variant-title') || '';
    /* Promote the bigger version of the upsell card image when one
       is available. Upsell card uses width=240 (72px display); cart
       row uses width=300 / 240 (120-150px display). Bumping the
       width param swap with regex when possible gives us a sharper
       ghost image at the cart row's display size. Falls back to
       the source URL if no width param is present. */
    var imageSrc = thumbImg ? thumbImg.getAttribute('src') : '';
    if (imageSrc) {
      imageSrc = imageSrc.replace(/width=\d+/, 'width=400');
    }
    var ctx = {
      variant_id: variantId,
      quantity: quantity,
      title: productTitle,
      url: productHandle ? '/products/' + productHandle : '#',
      image: imageSrc,
      image_alt: thumbImg ? (thumbImg.getAttribute('alt') || productTitle) : productTitle,
      image_height: thumbImg ? (thumbImg.getAttribute('height') || 150) : 150,
      price: priceCents,
      compare_at_price: compareAtCents,
      variant_title: variantTitle,
      has_only_default_variant: variantTitle === 'Default Title' || variantTitle === '',
      money_format: (window.Shopify && window.Shopify.money_format) || '${{amount}}'
    };

    var drawer = document.querySelector('cart-drawer');
    var drawerOpen = drawer && (drawer.classList.contains('active') || drawer.classList.contains('is-open'));
    var cartPageList = document.querySelector('#main-cart-items .hn-cart-page__items-list');

    /* SNAPSHOT existing-variant row BEFORE the ghost insertion
       (which may dedupe and mutate it). If the variant is already
       in the cart, the row gets BUMPED â€” and the cart-total delta
       isn't variant.price Ã— qty; it's (newDisplayed âˆ’ oldDisplayed)
       on the bumped row, since tier transitions can move it by an
       amount different from raw price Ã— qty.

       If the variant ISN'T in the cart yet, this snapshot is null
       and we fall back to the simple new-line formula:
         lineContribution = variant.price Ã— qty Ã— (1 âˆ’ cartPct/100) */
    var existingRow = null;
    var existingRowOldDisplayed = 0;
    var existingRowScope = drawerOpen ? drawer : document;
    if (variantId && existingRowScope) {
      existingRow = existingRowScope.querySelector(
        '[data-hn-variant-id="' + variantId + '"]' +
        ':not([data-hn-pdp-ghost])' +
        ':not([data-hn-cart-page-ghost])'
      );
      if (existingRow) {
        var existingRowPriceEl = findCartLinePriceEl(existingRow);
        if (existingRowPriceEl) {
          existingRowOldDisplayed = parseMoneyTextToCents(existingRowPriceEl.textContent);
        }
      }
    }

    if (drawerOpen && window.HnPdpCart) {
      try {
        state.drawerGhost = window.HnPdpCart.insertGhostRow(ctx);
      } catch (e) {}
    } else if (cartPageList) {
      try {
        state.pageGhost = insertCartPageGhostRow(ctx);
      } catch (e) {}
      // Bump the page heading "X Items" alongside the header badge
      // so both reflect the new count on the click frame.
      try {
        state.pageHeadingDelta = bumpCartPageHeading(quantity) || 0;
      } catch (e) {}
    }

    /* Compute totalDelta. Three cases:
         A. Dedupe with parseable old price: take the displayed
            line-price delta directly â€” captures any tier transition
            and any cart-level discount uniformly.
         B. Dedupe but old price won't parse: best-effort using raw
            price Ã— qty (under-counts the cart-level slice but stays
            directionally correct; section swap reconciles).
         C. Brand-new line: variant.price Ã— qty Ã— (1 âˆ’ cartPct/100). */
    var cartPct = readActiveCartDiscountPct();
    var totalDelta;
    if (existingRow) {
      if (existingRowOldDisplayed > 0) {
        var existingRowNewPriceEl = findCartLinePriceEl(existingRow);
        var existingRowNewDisplayed = existingRowNewPriceEl
          ? parseMoneyTextToCents(existingRowNewPriceEl.textContent)
          : existingRowOldDisplayed;
        totalDelta = existingRowNewDisplayed - existingRowOldDisplayed;
      } else {
        totalDelta = priceCents * quantity;
      }
    } else {
      var rawLineCents = priceCents * quantity;
      totalDelta = Math.round(rawLineCents * (100 - cartPct) / 100);
    }

    if (window.HnPdpCart) {
      try { state.countDelta = window.HnPdpCart.bumpCountBadge(quantity) || 0; } catch (e) {}
      if (totalDelta) {
        try { state.shipDelta = window.HnPdpCart.bumpShippingBar(totalDelta) || 0; } catch (e) {}
      }
    }

    if (totalDelta) {
      state.totalRevert = bumpCartTotal(totalDelta);
    }

    /* Total Savings delta = change_in_retail âˆ’ change_in_total.
       Total Savings is retail-based (includes compare-at markdown), so
       the retail change is MAX(compare_at, price) Ã— qty. compareAtCents
       was read off the card above; it's 0 when unset, so retail falls
       back to price for non-marked-down upsells. Section swap reconciles
       any small approximation in the displayed total. */
    var originalDelta = Math.max(priceCents, compareAtCents) * quantity;
    var savingsDelta = originalDelta - totalDelta;
    if (savingsDelta) {
      state.savingsRevert = bumpCartSavings(savingsDelta);
    }

    return state;
  }

  /* ============================================================
     Cart line qty change â€” optimistic UI

     Mirrors the upsell add pattern but for the stepper +/- on
     existing cart lines (and bundle chips). Updates the visible
     qty, line price, count badge, and shipbar on the click frame
     so the customer doesn't wait for the server section swap.
     ============================================================ */
  function parseMoneyTextToCents(text) {
    if (!text) return 0;
    // Match digits with optional thousands sep + 2-decimal portion
    var m = String(text).replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    var n = parseFloat(m);
    if (isNaN(n)) return 0;
    return Math.round(n * 100);
  }

  function findCartLinePriceEl(member) {
    // Drawer markup for a discounted line wraps two .price--end
    // elements: an <s class="cart-item__old-price price price--end">
    // (the strike) FIRST, then a <span class="price price--end">
    // (the final price). The naive ".price--end" selector returns
    // the strike first in DOM order, so optimistic updates were
    // mutating the strike instead of the line price. Force span
    // (or :not(s)) to skip the strike.
    // Cart page uses the unique .hn-cart-page__item-price-final.
    return (
      member.querySelector('.cart-item__price-wrapper span.price--end') ||
      member.querySelector('.hn-cart-page__item-price-final') ||
      // For bundle CHIPS inside a multi-variant bundle row, the
      // price element sits on the bundle row, not the chip. Walk
      // up to find it.
      (member.closest('[data-hn-bundle-product-id]') || document).querySelector('.cart-item__price-wrapper span.price--end')
    );
  }

  /* Optimistic cart-total update. Bumps every "cart total"
     display in the live DOM by the given delta (cents). Covers:
       - .totals__total-value          (drawer + cart-page totals)
       - [data-hn-cart-checkout-total] (checkout button amount)
     Returns a revert state callers can pass to revertCartTotal.

     Math intentionally raw â€” for percentage cart-level discounts
     the true delta is `unit_price Ã— ratio`, but
     we don't have the discount ratio in JS without extra plumbing.
     The ~500ms section swap reconciles to the canonical value
     either way, so a slightly-off mid-flight number beats a frozen
     one. */
  function bumpCartTotal(deltaCents) {
    var delta = parseInt(deltaCents, 10);
    if (!delta) return null;
    var selector = '.totals__total-value, [data-hn-cart-checkout-total]';
    var entries = [];
    document.querySelectorAll(selector).forEach(function (el) {
      var oldText = el.textContent;
      var oldCents = parseMoneyTextToCents(oldText);
      var newCents = oldCents + delta;
      if (newCents < 0) newCents = 0;
      // Preserve a trailing 3-letter currency code if present (e.g. " USD")
      var trailing = oldText.match(/\s+[A-Z]{3}\s*$/);
      var newText = formatMoneyCentsLocal(newCents);
      if (trailing) newText += trailing[0];
      el.textContent = newText;
      entries.push({ el: el, oldText: oldText });
    });
    return entries.length ? entries : null;
  }

  function revertCartTotal(entries) {
    if (!entries) return;
    entries.forEach(function (entry) {
      if (entry.el && entry.oldText != null) {
        entry.el.textContent = entry.oldText;
      }
    });
  }

  /* Optimistic Total Savings update. Targets .hn-cart-savings__amount
     in the drawer + cart-page summary. Three modes:
       text      â€” block exists, savings stays > 0: update the amount
       removed   â€” block exists, savings drops to 0: remove the block
       injected  â€” no block, savings becomes > 0: inject the block
                   before each .totals element (matches the Liquid
                   render's sibling position)
     Each entry remembers what it did so revertCartSavings can undo
     it on a network error.

     Caller computes the delta â€” typically:
       upsell add:   (compare_at - price) Ã— qty + cart-level slice
       qty change:   bundle tier delta + sale-strike delta + cart-level slice
       remove:       -(line_strike - line_effective + cart-level slice)
     Cart-level slice comes from readActiveCartDiscountPct(). The
     section swap that arrives ~350ms later reconciles to the
     canonical Shopify-computed value. */
  function bumpCartSavings(deltaCents) {
    var delta = parseInt(deltaCents, 10);
    if (!delta) return null;
    var entries = [];
    var existing = document.querySelectorAll('.hn-cart-savings__amount');

    if (existing.length > 0) {
      existing.forEach(function (el) {
        var oldText = el.textContent;
        var oldCents = parseMoneyTextToCents(oldText);
        var newCents = oldCents + delta;
        if (newCents <= 0) {
          // Drop the whole block â€” savings went to 0, matches the
          // Liquid render which omits the block in that state.
          var block = el.closest('.hn-cart-savings');
          if (block && block.parentNode) {
            entries.push({
              type: 'removed',
              block: block,
              parent: block.parentNode,
              nextSibling: block.nextSibling
            });
            block.parentNode.removeChild(block);
          }
        } else {
          el.textContent = formatMoneyCentsLocal(newCents);
          entries.push({ type: 'text', el: el, oldText: oldText });
        }
      });
    } else if (delta > 0) {
      // No existing block and savings is now positive â€” inject one
      // before each .totals element. Covers drawer + cart-page in
      // one pass since both surfaces use the same .totals class.
      var initialText = formatMoneyCentsLocal(delta);
      document.querySelectorAll('.totals').forEach(function (totals) {
        if (!totals.parentNode) return;
        var block = document.createElement('div');
        block.className = 'hn-cart-savings';
        block.setAttribute('role', 'status');
        block.setAttribute('aria-label', 'Total savings on this order');
        block.innerHTML =
          '<span class="hn-cart-savings__label">Total Savings</span>' +
          '<span class="hn-cart-savings__amount">' + initialText + '</span>';
        totals.parentNode.insertBefore(block, totals);
        entries.push({ type: 'injected', block: block });
      });
    }

    return entries.length ? entries : null;
  }

  function revertCartSavings(entries) {
    if (!entries) return;
    entries.forEach(function (entry) {
      if (entry.type === 'text' && entry.el && entry.oldText != null) {
        entry.el.textContent = entry.oldText;
      } else if (entry.type === 'removed' && entry.parent) {
        entry.parent.insertBefore(entry.block, entry.nextSibling);
      } else if (entry.type === 'injected' && entry.block && entry.block.parentNode) {
        entry.block.parentNode.removeChild(entry.block);
      }
    });
  }

  /* Read the customer's active cart-level percentage discount
     by walking the rendered chips (hn-cart-order-discounts
     marks each with data-hn-cart-level-discount). Format:
     "First order discount (25% OFF)". Returns the sum of all percentages
     active â€” typically just one. Used by the upsell/qty/remove
     optimistic paths to estimate the cart-level slice of the
     savings the new/removed line will contribute, so the
     displayed Total Savings + Estimated Total numbers stay
     close to the canonical values until the section swap
     arrives. */
  function readActiveCartDiscountPct() {
    var total = 0;
    /* CRITICAL: dedupe by chip text. hn-cart-order-discounts.liquid
       renders the SAME cart-level discount chip
       on EVERY line item, so a naive sum counts one 25% code as 50%
       on a 2-product cart, 75% on 3, 100% on 4 â€” which then divides
       the savings math by (100 - pct) = 0 and corrupts Total Savings.
       Dedupe by the chip's text the same way snapshotCartLevelDiscounts
       HTML does, so a single code counts once no matter how many rows
       display it. Two genuinely different codes still sum (their text
       differs). */
    var seen = {};
    document.querySelectorAll('[data-hn-cart-level-discount]').forEach(function (el) {
      var txt = (el.textContent || '').trim();
      if (!txt || seen[txt]) return;
      seen[txt] = true;
      var match = txt.match(/\((\d+)\s*%\s*OFF\)/i);
      if (match) total += parseInt(match[1], 10);
    });
    /* Safety clamp: the savings formula uses (100 - total) as a
       divisor, so a pathological stack of codes summing to >= 100
       would divide by zero or flip negative. Cap below 100; the
       server section swap reconciles the canonical value regardless. */
    if (total > 95) total = 95;
    return total;
  }

  function applyQtyChangeOptimistic(member, oldQty, newQty) {
    var state = {
      member: member,
      oldQty: oldQty,
      newQty: newQty,
      countDelta: 0,
      shipDelta: 0,
      priceEl: null,
      oldPriceText: null,
      chipQty: null,
      oldChipQtyText: null,
      chipValue: null,
      oldChipValueText: null
    };
    if (!member) return state;

    var delta = newQty - oldQty;

    // Stash old chip text for revert and update display.
    state.chipQty = member.querySelector('.hn-bundle-chip__qty');
    state.chipValue = member.querySelector('.hn-bundle-chip__value');
    if (state.chipQty) state.oldChipQtyText = state.chipQty.textContent;
    if (state.chipValue) state.oldChipValueText = state.chipValue.textContent;

    // newQty 0 = removal-style change. Update the visible chip text
    // and qty attr, then fade the row so it reads as "being removed"
    // immediately. For multi-variant bundles we fade the WRAPPER row
    // (chips are siblings inside a row that's about to lose one of
    // them â€” for a chip drop-to-0 the chip itself fades; if the last
    // chip is being zeroed, the wrapper effectively disappears via
    // section swap).
    if (state.chipQty) state.chipQty.textContent = newQty + 'Ã—';
    if (state.chipValue) state.chipValue.textContent = String(newQty);
    member.setAttribute('data-hn-variant-qty', String(newQty));

    if (newQty === 0) {
      state.fadedTargets = [];
      /* Fade the right element so the drop-to-0 reads as a removal
         on the click frame instead of showing "0Ã—" until commit.
         Multi-variant bundle: fade just the chip â€” its sibling
         chips and the wrapper stay visible.
         Single row: fade the entire row.
         priceContext / bundleWrapper are computed below; defer the
         fade decision until after that runs. We tag a marker here
         and the fade-apply happens just after priceContext setup. */
      state.fadeOnZero = true;
    }

    /* member is always the .hn-bundle-chip the user clicked inside
       (chip carries data-hn-variant-keys and sits closer than the
       row, so qtyBtn.closest picks the chip first). The row that
       owns the price/strike/bundle-tier-chip is the chip's nearest
       cart-item or hn-cart-page__item ancestor.

       Two topologies:
         1. Single row (regular non-bundle OR single-variant bundle)
            â€” priceContext is the row. Total qty = chip qty.
         2. Multi-variant bundle â€” priceContext is the wrapper row
            (which is also the chip's nearest cart-item ancestor).
            Total qty = sum of every chip inside the wrapper. */
    var priceContext = member.closest('tr.cart-item, li.hn-cart-page__item') || member;
    var bundleWrapper = priceContext.hasAttribute('data-hn-bundle-product-id') ? priceContext : null;
    var totalOldQty, totalNewQty;
    if (bundleWrapper) {
      var sumAfter = 0;
      bundleWrapper.querySelectorAll('[data-hn-variant-qty]').forEach(function (c) {
        sumAfter += parseInt(c.getAttribute('data-hn-variant-qty'), 10) || 0;
      });
      totalNewQty = sumAfter;
      totalOldQty = sumAfter - delta;
    } else {
      totalOldQty = oldQty;
      totalNewQty = newQty;
      // Keep the row's data-hn-variant-qty in sync with the chip
      // for single-row topologies (chip and row both render this
      // attr on the server, both should reflect the new qty so
      // future optimistic operations â€” e.g. upsell-add dedupe via
      // bumpHnRow â€” read a fresh value).
      if (priceContext !== member && priceContext.hasAttribute('data-hn-variant-qty')) {
        priceContext.setAttribute('data-hn-variant-qty', String(newQty));
      }
    }

    // Deferred fade-on-zero (see fadeOnZero marker above).
    if (state.fadeOnZero) {
      var fadeTarget = bundleWrapper ? member : priceContext;
      if (fadeTarget && !fadeTarget.classList.contains('is-removing')) {
        fadeTarget.classList.add('is-removing');
        state.fadedTargets.push(fadeTarget);
      }
    }

    /* LINE PRICE â€” proportional scaling, which matches Shopify checkout.

       Shopify checkout applies the line/product discount (the bundle
       tier) to the line FIRST, then the order discount to the
       already-discounted subtotal. That is SEQUENTIAL / multiplicative
       stacking, so 15% + 25% nets 36.25% off, not 40%. (The cart's
       settled prices come straight from Shopify's own discount_
       allocations, so they already equal checkout exactly; this
       optimistic step only has to predict the same model between a qty
       change and the server commit.)

       The displayed price already bakes in every discount, so the new
       price relates to the old by a clean ratio:

         new_displayed   newQty Ã— (100 - newTier)
         â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€  =  â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
         old_displayed   oldQty Ã— (100 - oldTier)

       The order-discount factor is identical on both sides and cancels
       (that IS the multiplicative model), so this is exact WITHIN a tier
       and ACROSS a tier crossing â€” the same number checkout lands on.
       No additive branch: checkout never stacks additively, so detecting
       it would only risk diverging from checkout.

       retailUnitCents (below) is the per-unit RETAIL price the savings
       delta scales by. */
    // Total Savings is retail-based (it includes a genuine compare-at
    // markdown), so the savings delta scales by MAX(compare_at, price),
    // not the charged unit price. compare_at is 0 when unset, so retail
    // falls back to the unit price for non-marked-down items.
    var unitPriceCents = parseInt(member.getAttribute('data-hn-unit-price'), 10) || 0;
    var compareAtUnitCents = parseInt(member.getAttribute('data-hn-compare-at-price'), 10) || 0;
    var retailUnitCents = Math.max(unitPriceCents, compareAtUnitCents);
    state.priceEl = findCartLinePriceEl(priceContext);
    var oldLineCents = 0;
    var newLineCents = 0;
    // Tier % only applies to bundle-eligible products. Non-eligible rows
    // get 0 on both sides â†’ pure qty scaling for the price and no bundle
    // chip injected below (the chip block is gated on newTierPct > 0).
    var rowBundleEligible = isRowBundleEligible(priceContext);
    var oldTierPct = rowBundleEligible ? pageBundleTierPctFor(totalOldQty) : 0;
    var newTierPct = rowBundleEligible ? pageBundleTierPctFor(totalNewQty) : 0;
    if (state.priceEl) {
      state.oldPriceText = state.priceEl.textContent;
      oldLineCents = parseMoneyTextToCents(state.oldPriceText);
      if (oldLineCents > 0 && totalOldQty > 0) {
        if (totalNewQty > 0) {
          // Robust anchor: scale the actual displayed price by qty +
          // tier. Captures every discount baked into the displayed
          // price, but re-rounds an already-rounded value so it can
          // drift ~1 cent per step.
          newLineCents = Math.round(
            oldLineCents * totalNewQty * (100 - newTierPct) /
            (totalOldQty * (100 - oldTierPct))
          );

          // Penny-accurate refinement: recompute from the exact unit
          // prices + the discounts we can see (bundle tier, then the
          // cart-level order %), rounding EACH allocation the way
          // Shopify does at checkout. We trust it only when it
          // reproduces the CURRENT displayed line to the exact cent â€”
          // that proves the model captures every active discount (no
          // unseen line discount, BOGO, etc.) AND that our rounding
          // matches Shopify's for this line. Otherwise we keep the
          // robust anchor. origNew sums each chip's real unitÃ—qty
          // (handles multi-variant bundles); origOld backs out the
          // stepped chip's delta.
          if (unitPriceCents > 0) {
            var orderPctLine = readActiveCartDiscountPct();
            var origNew = 0;
            priceContext.querySelectorAll('[data-hn-unit-price]').forEach(function (c) {
              var cu = parseInt(c.getAttribute('data-hn-unit-price'), 10) || 0;
              var cq = parseInt(c.getAttribute('data-hn-variant-qty'), 10) || 0;
              origNew += cu * cq;
            });
            if (origNew === 0) origNew = unitPriceCents * totalNewQty;
            var origOld = origNew - unitPriceCents * delta;
            if (origOld > 0 &&
                exactLineCents(origOld, oldTierPct, orderPctLine) === oldLineCents) {
              newLineCents = exactLineCents(origNew, newTierPct, orderPctLine);
            }
          }
        }
        state.priceEl.textContent = formatMoneyCentsLocal(newLineCents);
      }
    }

    var totalDelta = newLineCents - oldLineCents;

    /* Cart-level total refinement (only matters with a cart-wide order
       % discount). Shopify rounds that discount ONCE on the whole
       subtotal-after-line-discounts and distributes it, so stepping one
       line shifts the rounding across the cart â€” the cart total does NOT
       change by exactly the stepped line's change. Recompute the way
       Shopify does, from cart.items_subtotal_price (rendered as
       data-hn-items-subtotal): newSubtotal = old + this line's
       afterBundle delta; newTotal = newSubtotal âˆ’ round(newSubtotal Ã—
       code%). Trust it ONLY when it reproduces the CURRENT displayed
       total to the exact cent (model captures every cart-level discount
       + rounding matches Shopify); otherwise keep the line-delta. With
       no code, or a single-item cart, this resolves to the same line
       delta â€” so it can't regress those. */
    if (unitPriceCents > 0) {
      var orderPctForTotal = readActiveCartDiscountPct();
      var subEl = document.querySelector('[data-hn-items-subtotal]');
      var totalValEl = document.querySelector('.totals__total-value');
      if (orderPctForTotal > 0 && orderPctForTotal < 100 && subEl && totalValEl) {
        var oldSubtotal = parseInt(subEl.getAttribute('data-hn-items-subtotal'), 10);
        var displayedTotal = parseMoneyTextToCents(totalValEl.textContent);
        if (oldSubtotal > 0 && displayedTotal > 0) {
          // This line's pre-discount original (Î£ chips' real unitÃ—qty)
          // at the new and old qty, then its bundle-tier-discounted
          // subtotal contribution (afterBundle) â€” the same quantities
          // the exact line price used above.
          var lineOrigNew = 0;
          priceContext.querySelectorAll('[data-hn-unit-price]').forEach(function (c) {
            lineOrigNew += (parseInt(c.getAttribute('data-hn-unit-price'), 10) || 0) *
                           (parseInt(c.getAttribute('data-hn-variant-qty'), 10) || 0);
          });
          if (lineOrigNew === 0) lineOrigNew = unitPriceCents * totalNewQty;
          var lineOrigOld = lineOrigNew - unitPriceCents * delta;
          if (lineOrigOld > 0) {
            // Floor each step, matching Shopify's customer-favorable
            // rounding (see exactLineCents).
            var afterBundleNew = Math.floor(lineOrigNew * (100 - newTierPct) / 100);
            var afterBundleOld = Math.floor(lineOrigOld * (100 - oldTierPct) / 100);
            var newSubtotal = oldSubtotal + (afterBundleNew - afterBundleOld);
            var exactOldTotal = Math.floor(oldSubtotal * (100 - orderPctForTotal) / 100);
            if (exactOldTotal === displayedTotal) {
              var exactNewTotal = Math.floor(newSubtotal * (100 - orderPctForTotal) / 100);
              totalDelta = exactNewTotal - displayedTotal;
              // Advance the stored subtotal so multi-step stepping stays
              // exact, and keep every surface's copy in sync.
              document.querySelectorAll('[data-hn-items-subtotal]').forEach(function (el) {
                el.setAttribute('data-hn-items-subtotal', String(newSubtotal));
              });
            }
          }
        }
      }
    }

    // Count badge + shipbar. Shipbar uses cart.total_price internally,
    // so its delta equals the line's totalDelta exactly.
    if (window.HnPdpCart) {
      try { state.countDelta = window.HnPdpCart.bumpCountBadge(delta) || 0; } catch (e) {}
      if (totalDelta) {
        try { state.shipDelta = window.HnPdpCart.bumpShippingBar(totalDelta) || 0; } catch (e) {}
      }
    }

    // Cart-page "N Items" heading bump (no-op when not on /cart).
    try { state.pageHeadingDelta = bumpCartPageHeading(delta) || 0; } catch (e) {}

    // Strike total: strike doesn't include line-level discounts, so
    // it's just (strike_per_unit Ã— newTotalQty). Cancels naturally
    // when the line has no strike.
    var strikeEl = priceContext.querySelector('.cart-item__old-price, .hn-cart-page__item-price-original');
    if (strikeEl && totalOldQty > 0) {
      var oldStrikeText = strikeEl.textContent;
      var oldStrikeCents = parseMoneyTextToCents(oldStrikeText);
      if (oldStrikeCents > 0) {
        var strikePerUnit = Math.round(oldStrikeCents / totalOldQty);
        state.strikeEl = strikeEl;
        state.oldStrikeText = oldStrikeText;
        strikeEl.textContent = formatMoneyCentsLocal(strikePerUnit * totalNewQty);
      }
    }

    // Cart total + checkout button amount. Single line-delta read,
    // no per-discount math needed.
    if (totalDelta) {
      state.totalRevert = bumpCartTotal(totalDelta);
    }

    // Total Savings delta = change_in_retail âˆ’ change_in_total.
    //   change_in_retail = retailUnitCents Ã— delta_qty
    //   change_in_total  = totalDelta (the displayed line delta).
    // retailUnitCents (= MAX(compare_at, price), from the chip attrs) is
    // the exact per-unit retail price rendered by Liquid, NOT reverse-
    // engineered from the displayed discounted price. The old back-out
    // divided by (100-tier)(100-cartPct), which silently assumed a
    // multiplicative stack and broke whenever the real allocation
    // differed. Scaling by the real retail unit makes this delta equal
    // exactly how the footer's Total Savings (retail_total âˆ’ total_price)
    // moves â€” so it stays consistent with the row strikes, the line, and
    // the Estimated Total under ANY stacking model, AND now includes the
    // compare-at markdown (e.g. the toiletry bag's $19.99 â†’ $9.99).
    // (member is the chip the user stepped; for multi-variant bundles
    // that's the single variant whose qty changed, which is exactly the
    // variant whose retail contribution grows/shrinks.)
    var savingsDelta = 0;
    if (retailUnitCents > 0 && delta) {
      var originalDelta = retailUnitCents * delta;
      savingsDelta = originalDelta - totalDelta;
    }
    if (savingsDelta) {
      state.savingsRevert = bumpCartSavings(savingsDelta);
    }

    syncProductQtyButtons(member);

    // Sync the synthetic bundle chip on the priceContext's
    // .discounts ul so the displayed discount label tracks the new
    // tier (and uses the TOTAL qty for multi-variant bundles, not
    // the individual chip qty). Three cases:
    //   newTierPct > 0 + existing chip      â†’ replace its text
    //   newTierPct > 0 + no existing chip   â†’ insert a new chip
    //   newTierPct = 0 + existing chip      â†’ remove the chip
    state.bundleChipReplaced = null;
    state.bundleChipInserted = null;
    state.bundleChipRemoved = null;
    var rowDiscountsUl = priceContext.querySelector('ul.discounts');
    var existingBundleChip = null;
    if (rowDiscountsUl) {
      rowDiscountsUl.querySelectorAll('li.discounts__discount').forEach(function (li) {
        var txt = (li.textContent || '').trim();
        if (!existingBundleChip && /^\d+\s+PACK\b/i.test(txt)) {
          existingBundleChip = li;
        }
      });
    }
    var bundleIconSvg = '<svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></svg> ';
    if (newTierPct > 0 && totalNewQty > 0) {
      var bundleChipText = totalNewQty + ' Pack (' + newTierPct + '% OFF)';
      if (existingBundleChip) {
        state.bundleChipReplaced = { li: existingBundleChip, oldHTML: existingBundleChip.innerHTML };
        existingBundleChip.innerHTML = bundleIconSvg + bundleChipText;
      } else {
        var newLi = document.createElement('li');
        newLi.className = 'discounts__discount';
        newLi.innerHTML = bundleIconSvg + bundleChipText;
        if (rowDiscountsUl) {
          rowDiscountsUl.insertBefore(newLi, rowDiscountsUl.firstChild);
          state.bundleChipInserted = { li: newLi };
        } else {
          var detailsCol = priceContext.querySelector('.cart-item__details, .hn-cart-page__item-details');
          if (detailsCol) {
            var ul = document.createElement('ul');
            ul.className = 'discounts list-unstyled';
            ul.setAttribute('role', 'list');
            ul.setAttribute('aria-label', 'Discount');
            ul.appendChild(newLi);
            detailsCol.insertBefore(ul, detailsCol.firstChild);
            state.bundleChipInserted = { li: newLi, ul: ul, createdUl: true };
          }
        }
      }
    } else if (existingBundleChip) {
      // qty fell below the lowest tier â€” remove the chip
      state.bundleChipRemoved = {
        li: existingBundleChip,
        parent: existingBundleChip.parentNode,
        nextSibling: existingBundleChip.nextSibling
      };
      existingBundleChip.parentNode.removeChild(existingBundleChip);
    }

    return state;
  }

  function revertQtyChangeOptimistic(state) {
    if (!state || !state.member) return;
    if (state.chipQty && state.oldChipQtyText != null) {
      state.chipQty.textContent = state.oldChipQtyText;
    }
    if (state.chipValue && state.oldChipValueText != null) {
      state.chipValue.textContent = state.oldChipValueText;
    }
    if (state.priceEl && state.oldPriceText != null) {
      state.priceEl.textContent = state.oldPriceText;
    }
    state.member.setAttribute('data-hn-variant-qty', String(state.oldQty));
    syncProductQtyButtons(state.member);
    if (state.countDelta && window.HnPdpCart) {
      try { window.HnPdpCart.revertCountBadge(state.countDelta); } catch (e) {}
    }
    if (state.shipDelta && window.HnPdpCart) {
      try { window.HnPdpCart.revertShippingBar(state.shipDelta); } catch (e) {}
    }
    revertCartTotal(state.totalRevert);
    revertCartSavings(state.savingsRevert);
    if (state.pageHeadingDelta) {
      try { revertCartPageHeading(state.pageHeadingDelta); } catch (e) {}
    }
    if (state.strikeEl && state.oldStrikeText != null) {
      state.strikeEl.textContent = state.oldStrikeText;
    }
    // Bundle chip revert (one of these three branches may apply,
    // depending on which case fired during apply).
    if (state.bundleChipReplaced) {
      state.bundleChipReplaced.li.innerHTML = state.bundleChipReplaced.oldHTML;
    }
    if (state.bundleChipInserted) {
      var info = state.bundleChipInserted;
      if (info.createdUl && info.ul && info.ul.parentNode) {
        info.ul.parentNode.removeChild(info.ul);
      } else if (info.li && info.li.parentNode) {
        info.li.parentNode.removeChild(info.li);
      }
    }
    if (state.bundleChipRemoved) {
      var info2 = state.bundleChipRemoved;
      if (info2.parent) {
        info2.parent.insertBefore(info2.li, info2.nextSibling);
      }
    }
    if (state.fadedTargets) {
      state.fadedTargets.forEach(function (t) {
        t.classList.remove('is-removing');
      });
    }
  }

  /* ============================================================
     Click-outside qty commit

     The user can mash +/- on a row as long as they want without
     committing. Commit fires when:
       1. Click lands OUTSIDE the row (handled in the top-of-file
          delegated click listener via commitPendingQtyIfOutside)
       2. Long safety-net timer fires (15s â€” covers walked-away
          carts so an abandoned change still saves)
     Each row's pending state lives in a regular Map so we can
     iterate it during the outside-click sweep.
     ============================================================ */
  var qtyPendingCommits = new Map();
  var QTY_SAFETY_TIMEOUT_MS = 15000;

  /* Drop a member's pending qty commit (timer + map entry) without
     firing it â€” used when a drop-to-0 takes over and commits the
     removal immediately, so the stale timer can't fire a duplicate
     POST afterward. */
  function cancelPendingQtyCommit(member) {
    var pending = qtyPendingCommits.get(member);
    if (pending) {
      if (pending.safetyTimer) clearTimeout(pending.safetyTimer);
      qtyPendingCommits.delete(member);
    }
  }

  function scheduleQtyCommit(member, keys) {
    if (!member) return;
    var existing = qtyPendingCommits.get(member);
    if (existing && existing.safetyTimer) clearTimeout(existing.safetyTimer);

    var pending = { keys: keys, safetyTimer: null };
    pending.safetyTimer = setTimeout(function () {
      if (qtyPendingCommits.get(member) === pending) {
        qtyPendingCommits.delete(member);
        commitQtyChange(member, keys);
      }
    }, QTY_SAFETY_TIMEOUT_MS);
    qtyPendingCommits.set(member, pending);
  }

  /* Walks pending commits and fires the ones whose row does NOT
     contain the given target. Called from the document click
     listener â€” clicking inside a row keeps its commit pending,
     clicking anywhere else commits it. */
  function commitPendingQtyIfOutside(target) {
    if (!qtyPendingCommits.size) return [];
    var toCommit = [];
    qtyPendingCommits.forEach(function (pending, member) {
      if (!member.contains(target)) {
        toCommit.push({ member: member, pending: pending });
      }
    });
    var commits = [];
    toCommit.forEach(function (entry) {
      clearTimeout(entry.pending.safetyTimer);
      qtyPendingCommits.delete(entry.member);
      var promise = commitQtyChange(entry.member, entry.pending.keys);
      if (promise && typeof promise.then === 'function') commits.push(promise);
    });
    return commits;
  }

  function commitQtyChange(member, keys) {
    if (!member) return null;
    var finalQty = parseInt(member.getAttribute('data-hn-variant-qty'), 10);
    if (isNaN(finalQty) || finalQty < 0) finalQty = 0;

    member.classList.add('is-loading');
    if (finalQty === 0) member.classList.add('is-removing');
    setMemberBusy(member, true);

    var updates = buildConsolidatedUpdates(keys, finalQty);
    return postUpdates(updates, function onError() {
      // Best-effort: just lift loading state. The optimistic UI
      // stays as-is; the next user interaction's section swap
      // (or a page reload) will reconcile against canonical
      // server state. Detailed multi-click revert is intentionally
      // skipped to keep the commit path simple.
      member.classList.remove('is-loading', 'is-removing');
      setMemberBusy(member, false);
    }, function onSuccess() {
      recordCartLineEvent(finalQty === 0 ? 'hi_cart_item_remove' : 'hi_cart_qty_change', keys, finalQty);
    });
  }

  /* ============================================================
     Cart line remove â€” optimistic UI

     Collapses the row visually (CSS hooks .is-removing, .is-loading
     already in place) and decrements the cart-count badge + shipbar
     by the row's contribution BEFORE the network round-trip. The
     section swap on success replaces the whole items list (so the
     row stays gone). On error we revert.
     ============================================================ */
  function applyRemoveOptimistic(member) {
    var state = {
      member: member,
      qty: 0,
      countDelta: 0,
      shipDelta: 0,
      lineCents: 0
    };
    if (!member) return state;

    // Total qty being removed. For multi-variant bundle rows the
    // row aggregates its chips so we walk them; for single rows
    // the data-hn-variant-qty attr is the answer.
    var totalQty = 0;
    if (member.hasAttribute('data-hn-bundle-product-id')) {
      member.querySelectorAll('[data-hn-variant-qty]').forEach(function (chip) {
        totalQty += parseInt(chip.getAttribute('data-hn-variant-qty'), 10) || 0;
      });
    } else {
      totalQty = parseInt(member.getAttribute('data-hn-variant-qty'), 10) || 1;
    }
    state.qty = totalQty;

    // Line price (cents) â€” for shipbar bump.
    var priceEl = findCartLinePriceEl(member);
    if (priceEl) {
      state.lineCents = parseMoneyTextToCents(priceEl.textContent);
    }

    if (window.HnPdpCart) {
      try { state.countDelta = window.HnPdpCart.bumpCountBadge(-totalQty) || 0; } catch (e) {}
      if (state.lineCents) {
        try { state.shipDelta = window.HnPdpCart.bumpShippingBar(-state.lineCents) || 0; } catch (e) {}
      }
    }

    // Cart-page "N Items" heading decrement (no-op when not on /cart).
    try { state.pageHeadingDelta = bumpCartPageHeading(-totalQty) || 0; } catch (e) {}

    // Estimated Total: the displayed line price already equals this
    // line's contribution to cart.total_price (it's net of every
    // discount Shopify allocated). Removing the line drops the total
    // by exactly that amount.
    if (state.lineCents) {
      state.totalRevert = bumpCartTotal(-state.lineCents);
    }

    // Total Savings: the line's contribution to cart_savings is
    // (original_line âˆ’ displayed). original_line = Î£ variant.price Ã— qty
    // across the line(s) being removed. We sum each chip's real
    // data-hn-unit-price Ã— its qty (exact, from Liquid) instead of
    // reverse-engineering variant.price out of the displayed discounted
    // price â€” the old back-out assumed multiplicative discount stacking
    // and produced a wrong unit price (hence wrong savings) whenever
    // Shopify stacked the bundle % and order % any other way. For a
    // per-variant remove `member` is the row (one chip); for a bundle
    // remove it's the wrapper (every variant chip) â€” summing chips
    // covers both. Model-agnostic and consistent with the qty-change
    // path.
    // Retail line total = Î£ MAX(compare_at, price) Ã— qty, so removing a
    // marked-down item drops Total Savings by the markdown-inclusive
    // amount (Total Savings is retail-based). compare_at is 0 when unset.
    var originalLineCents = 0;
    member.querySelectorAll('[data-hn-unit-price]').forEach(function (chip) {
      var u = parseInt(chip.getAttribute('data-hn-unit-price'), 10) || 0;
      var ca = parseInt(chip.getAttribute('data-hn-compare-at-price'), 10) || 0;
      var q = parseInt(chip.getAttribute('data-hn-variant-qty'), 10) || 0;
      originalLineCents += Math.max(u, ca) * q;
    });
    if (originalLineCents > 0 && state.lineCents) {
      var lineCartSavings = originalLineCents - state.lineCents;
      if (lineCartSavings > 0) {
        state.savingsRevert = bumpCartSavings(-lineCartSavings);
      }
    }

    return state;
  }

  function revertRemoveOptimistic(state) {
    if (!state) return;
    if (state.countDelta && window.HnPdpCart) {
      try { window.HnPdpCart.revertCountBadge(state.countDelta); } catch (e) {}
    }
    if (state.shipDelta && window.HnPdpCart) {
      try { window.HnPdpCart.revertShippingBar(state.shipDelta); } catch (e) {}
    }
    revertCartTotal(state.totalRevert);
    revertCartSavings(state.savingsRevert);
    if (state.pageHeadingDelta) {
      try { revertCartPageHeading(state.pageHeadingDelta); } catch (e) {}
    }
  }

  function revertUpsellOptimistic(state) {
    if (!state) return;
    if (state.drawerGhost && window.HnPdpCart) {
      try { window.HnPdpCart.revertGhostRow(state.drawerGhost.row, state.drawerGhost.revert); } catch (e) {}
    }
    if (state.pageGhost) {
      try { revertCartPageGhostRow(state.pageGhost); } catch (e) {}
    }
    if (state.pageHeadingDelta) {
      try { revertCartPageHeading(state.pageHeadingDelta); } catch (e) {}
    }
    if (state.countDelta && window.HnPdpCart) {
      try { window.HnPdpCart.revertCountBadge(state.countDelta); } catch (e) {}
    }
    if (state.shipDelta && window.HnPdpCart) {
      try { window.HnPdpCart.revertShippingBar(state.shipDelta); } catch (e) {}
    }
    revertCartTotal(state.totalRevert);
    revertCartSavings(state.savingsRevert);
  }

  /* ============================================================
     /cart page ghost-row insertion

     Mirrors the markup pattern from sections/main-cart-items.liquid
     (regular non-bundle row): <li class="hn-cart-page__item
     cart-item"> with thumb, details (title + collapsed chip stepper),
     totals (price + Remove). Inserted at the TOP of
     .hn-cart-page__items-list. The section swap that happens after
     /cart/add.js returns will replace this with the canonical
     server-rendered row.

     Tagged with data-hn-cart-page-ghost so revert can find and
     remove it without affecting any canonical row that might have
     landed mid-flight.
     ============================================================ */
  function escapeHtmlLocal(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoneyCentsLocal(cents) {
    if (window.HnPdpCart && typeof window.HnPdpCart.formatMoneyCents === 'function') {
      return window.HnPdpCart.formatMoneyCents(cents);
    }
    return '$' + (parseInt(cents, 10) / 100).toFixed(2);
  }

  /* Parses window.__hnBundleTiers (set in theme.liquid via
     hn-bundle-tiers.liquid) once and caches the result for
     this IIFE. Mirrors getBundleTiers in
     hn-pdp-cart-optimistic.js but is duplicated here because
     the two files are independent IIFEs that can't share
     closures. */
  var _pageBundleTiersCache = null;
  function pageGetBundleTiers() {
    if (_pageBundleTiersCache !== null) return _pageBundleTiersCache;
    _pageBundleTiersCache = false;
    var raw = window.__hnBundleTiers;
    if (typeof raw !== 'string' || !raw) return null;
    var parts = raw.split('|');
    if (parts.length < 2 || !parts[0] || !parts[1]) return null;
    var qtys = parts[0].split(',').map(function (s) { return parseInt(s, 10); }).filter(Boolean);
    var pcts = parts[1].split(',').map(function (s) { return parseInt(s, 10); }).filter(function (n) { return !isNaN(n); });
    if (!qtys.length || qtys.length !== pcts.length) return null;
    _pageBundleTiersCache = { qtys: qtys, pcts: pcts };
    return _pageBundleTiersCache;
  }

  function pageBundleTierPctFor(qty) {
    var t = pageGetBundleTiers();
    if (!t) return 0;
    var pct = 0;
    for (var i = 0; i < t.qtys.length; i++) {
      if (qty >= t.qtys[i]) pct = t.pcts[i];
    }
    return pct;
  }

  /* Whether a cart row's PRODUCT is bundle-eligible. The tier ladder
     applies by quantity alone, so without this gate stepping a NON-
     eligible product (e.g. a toiletry bag) to qty 2+ would optimistically
     apply a tier % and inject a phantom "X Pack (Y% OFF)" chip that the
     server immediately strips â€” a visible flicker. Eligibility signals:
       - data-hn-bundle-eligible="true"  regular rows whose product is
         tagged bundle-pricing / has the metafield (set in Liquid)
       - data-hn-bundle-product-id       multi-variant combined bundle row
       - .cart-item--bundle-member       single-variant bundle row
     Combined/member rows are inherently eligible; only regular rows need
     the explicit attr to distinguish "eligible product currently at qty 1"
     from "not a bundle product at all". */
  function isRowBundleEligible(row) {
    if (!row) return false;
    return (
      row.getAttribute('data-hn-bundle-eligible') === 'true' ||
      row.hasAttribute('data-hn-bundle-product-id') ||
      (row.classList && row.classList.contains('cart-item--bundle-member'))
    );
  }

  /* Replicate Shopify checkout's per-allocation rounding for one line.
     Shopify applies the line/product discount (bundle tier) first, then
     the cart-level order % to the remainder â€” sequential, rounding each
     step. Crucially it rounds the discounted price DOWN (floors it,
     i.e. rounds the discount UP, in the customer's favor): 3 Ã— $9.99 at
     25% off is floor($22.4775) = $22.47, NOT round() = $22.48. So we
     floor at each step. The one-shot floor(orig Ã— (1-tier)(1-order))
     can differ by a cent from flooring each allocation, so we mirror
     the two sequential floored subtractions. origCents = the line's
     pre-discount original (Î£ unit Ã— qty). Returns effective line cents.
     The caller only trusts this when it reproduces the CURRENT displayed
     line to the exact cent, so if a given store/line rounds differently
     it safely falls back to the proportional anchor. */
  function exactLineCents(origCents, tierPct, orderPct) {
    var afterBundle = Math.floor(origCents * (100 - tierPct) / 100);
    return Math.floor(afterBundle * (100 - orderPct) / 100);
  }

  /* Snapshot the customer's active cart-level discount chips
     so the optimistic ghost row gets
     the SAME chips as the canonical rows. Walks ANY existing
     cart row's .discounts ul and dedupes by chip title, since
     cart-level codes apply identically to every line.
     Returns an HTML string of <li> elements ready to inject;
     empty string when no cart-level codes are active. */
  function snapshotCartLevelDiscountsHTML() {
    var seen = {};
    var out = '';
    document.querySelectorAll('[data-hn-cart-level-discount]').forEach(function (li) {
      var key = (li.textContent || '').trim();
      if (!key || seen[key]) return;
      seen[key] = true;
      out += li.outerHTML;
    });
    return out;
  }

  function insertCartPageGhostRow(ctx) {
    var list = document.querySelector('#main-cart-items .hn-cart-page__items-list');
    if (!list) return null;

    var qty = parseInt(ctx.quantity, 10) || 1;
    var unitPrice = parseInt(ctx.price, 10) || 0;

    /* Dedupe pass â€” if the variant being added is ALREADY on the
       cart page, bump the existing row's qty + price in place
       instead of inserting a fresh ghost. Matches the drawer
       behavior via HnPdpCart.insertGhostRow + findExistingRow.
       Without this, the upsell button would stack a stripped-
       down ghost row above the canonical row even though the
       customer already has the variant. */
    var variantId = parseInt(ctx.variant_id, 10);
    if (variantId) {
      var existing = list.querySelector(
        '[data-hn-variant-id="' + variantId + '"]:not([data-hn-cart-page-ghost])'
      );
      if (existing) {
        var oldQty = parseInt(existing.getAttribute('data-hn-variant-qty'), 10) || 1;
        var newQty = oldQty + qty;
        var chipQty = existing.querySelector('.hn-bundle-chip__qty');
        var chipValue = existing.querySelector('.hn-bundle-chip__value');
        var priceEl = existing.querySelector('.hn-cart-page__item-price-final');

        var oldChipQtyText = chipQty ? chipQty.textContent : null;
        var oldChipValueText = chipValue ? chipValue.textContent : null;
        var oldPriceText = priceEl ? priceEl.textContent : null;

        existing.setAttribute('data-hn-variant-qty', String(newQty));
        if (chipQty) chipQty.textContent = newQty + 'Ã—'; /* Ã— */
        if (chipValue) chipValue.textContent = String(newQty);

        /* Apply bundle tier discount via the proportional method
           so any cart-level percentage discount already in the
           displayed price stays correct. Mirrors bumpHnRow in
           hn-pdp-cart-optimistic.js. Tier only applies to bundle-
           eligible products â€” otherwise 0 on both sides (pure qty
           scaling) and no phantom pack chip injected below. */
        var pageEligible = isRowBundleEligible(existing);
        var pageOldTierPct = pageEligible ? pageBundleTierPctFor(oldQty) : 0;
        var pageNewTierPct = pageEligible ? pageBundleTierPctFor(newQty) : 0;
        if (priceEl) {
          var pageOldLineCents = oldPriceText ? parseMoneyTextToCents(oldPriceText) : 0;
          if (pageOldLineCents > 0) {
            var pageNewLineCents = Math.round(
              pageOldLineCents * newQty * (100 - pageNewTierPct) /
              (oldQty * (100 - pageOldTierPct))
            );
            priceEl.textContent = formatMoneyCentsLocal(pageNewLineCents);
          } else if (unitPrice) {
            // Fallback when old text won't parse â€” best-effort raw
            // (ignores cart-level codes).
            var pageFallbackCents = Math.round(unitPrice * newQty * (100 - pageNewTierPct) / 100);
            priceEl.textContent = formatMoneyCentsLocal(pageFallbackCents);
          }
        }

        /* Inject / update the synthetic bundle chip on the row so
           the discount labels match. Same chip text format as the
           Liquid render: "{N} PACK ({X}% OFF)". */
        var bundleChipReplaced = null;
        var bundleChipInserted = null;
        if (pageNewTierPct > 0) {
          /* Match the Liquid render: "{N} Pack ({X}% OFF)".
             CSS uppercase handles visual; underlying text stays
             mixed-case so the regex below finds the canonical
             chip when one already exists. */
          var bundleChipText = newQty + ' Pack (' + pageNewTierPct + '% OFF)';
          var rowDiscountsUl = existing.querySelector('ul.discounts');
          var existingBundleChip = null;
          if (rowDiscountsUl) {
            rowDiscountsUl.querySelectorAll('li.discounts__discount').forEach(function (li) {
              var txt = (li.textContent || '').trim();
              if (!existingBundleChip && /^\d+\s+PACK\b/i.test(txt)) {
                existingBundleChip = li;
              }
            });
          }
          var iconSvg = '<svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></svg> ';
          if (existingBundleChip) {
            bundleChipReplaced = { li: existingBundleChip, oldHTML: existingBundleChip.innerHTML };
            existingBundleChip.innerHTML = iconSvg + bundleChipText;
          } else {
            var newLi = document.createElement('li');
            newLi.className = 'discounts__discount';
            newLi.innerHTML = iconSvg + bundleChipText;
            if (rowDiscountsUl) {
              rowDiscountsUl.insertBefore(newLi, rowDiscountsUl.firstChild);
              bundleChipInserted = { li: newLi, ul: rowDiscountsUl };
            } else {
              var detailsCol = existing.querySelector('.hn-cart-page__item-details');
              if (detailsCol) {
                var ul = document.createElement('ul');
                ul.className = 'discounts list-unstyled';
                ul.setAttribute('role', 'list');
                ul.setAttribute('aria-label', 'Discount');
                ul.appendChild(newLi);
                detailsCol.insertBefore(ul, detailsCol.firstChild);
                bundleChipInserted = { li: newLi, ul: ul, createdUl: true };
              }
            }
          }
        }

        return {
          type: 'hn-bumped-page',
          row: existing,
          oldQty: oldQty,
          oldChipQtyText: oldChipQtyText,
          oldChipValueText: oldChipValueText,
          oldPriceText: oldPriceText,
          chipQty: chipQty,
          chipValue: chipValue,
          priceEl: priceEl,
          bundleChipReplaced: bundleChipReplaced,
          bundleChipInserted: bundleChipInserted
        };
      }
    }

    var linePrice = unitPrice * qty;
    var compareAt = parseInt(ctx.compare_at_price, 10) || 0;
    var showStrike = compareAt > unitPrice && compareAt > 0;
    var strikeLine = showStrike ? compareAt * qty : 0;
    var salePct = showStrike ? Math.round(((compareAt - unitPrice) / compareAt) * 100) : 0;
    var showVariant = ctx.variant_title && !ctx.has_only_default_variant;
    var variantLabel = showVariant ? ctx.variant_title : (qty === 1 ? 'Item' : 'Items');

    var imgHTML = '';
    if (ctx.image) {
      imgHTML =
        '<a href="' + escapeHtmlLocal(ctx.url) + '" class="hn-cart-page__item-thumb" tabindex="-1" aria-hidden="true">' +
          '<img src="' + escapeHtmlLocal(ctx.image) +
            '" alt="' + escapeHtmlLocal(ctx.image_alt) +
            '" loading="lazy" width="120" height="' + escapeHtmlLocal(ctx.image_height || 120) + '">' +
        '</a>';
    }

    /* Discount chips on the ghost row.
       1. Synthetic SALE chip when compare_at > price (retail
          markdown). Captured directly from the upsell ctx.
       2. Cart-level discounts cloned from existing
          rows via snapshotCartLevelDiscountsHTML so the ghost
          matches what the canonical row will show â€” without
          waiting for the section swap to arrive. */
    var saleHTML = '';
    if (showStrike) {
      saleHTML =
        '<li class="discounts__discount">' +
          '<svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12">' +
            '<path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor">' +
          '</svg>' +
          ' SALE (' + salePct + '% OFF)' +
        '</li>';
    }
    var cartLevelHTML = snapshotCartLevelDiscountsHTML();
    var discountsHTML = '';
    if (saleHTML || cartLevelHTML) {
      discountsHTML =
        '<ul class="discounts list-unstyled" role="list" aria-label="Discount">' +
          saleHTML + cartLevelHTML +
        '</ul>';
    }

    var priceHTML = '';
    if (showStrike) {
      priceHTML =
        '<s class="hn-cart-page__item-price-original">' + formatMoneyCentsLocal(strikeLine) + '</s>' +
        '<span class="hn-cart-page__item-price-final">' + formatMoneyCentsLocal(linePrice) + '</span>';
    } else {
      priceHTML =
        '<span class="hn-cart-page__item-price-final">' + formatMoneyCentsLocal(linePrice) + '</span>';
    }

    var html =
      '<li class="hn-cart-page__item cart-item" data-hn-cart-page-ghost data-hn-ghost-variant-id="' + escapeHtmlLocal(ctx.variant_id) + '">' +
        imgHTML +
        '<div class="hn-cart-page__item-details">' +
          discountsHTML +
          '<a href="' + escapeHtmlLocal(ctx.url) + '" class="hn-cart-page__item-title cart-item__name">' +
            escapeHtmlLocal(ctx.title) +
          '</a>' +
          '<div class="hn-bundle-chips" role="list">' +
            '<div class="hn-bundle-chip" role="listitem">' +
              '<button type="button" class="hn-bundle-chip__face hn-bundle-chip__face--collapsed" disabled aria-label="Adding to cart">' +
                '<span class="hn-bundle-chip__qty">' + qty + '&times;</span>' +
                '<span class="hn-bundle-chip__name">' + escapeHtmlLocal(variantLabel) + '</span>' +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="hn-cart-page__item-totals">' +
          '<div class="hn-cart-page__item-price">' +
            priceHTML +
          '</div>' +
          '<button type="button" class="hn-cart-page__remove" disabled aria-label="Adding to cart">Remove</button>' +
        '</div>' +
      '</li>';

    list.insertAdjacentHTML('afterbegin', html);
    var ghost = list.querySelector('li[data-hn-cart-page-ghost]');

    // If the cart was empty before this add, the <cart-items>
    // wrapper has class is-empty which hides the items list via
    // CSS. Section swap handles this canonically on response, but
    // we toggle it now so the ghost is visible immediately.
    var cartItems = document.querySelector('cart-items');
    var wasEmpty = cartItems && cartItems.classList.contains('is-empty');
    if (wasEmpty) cartItems.classList.remove('is-empty');

    return { row: ghost, wasEmpty: wasEmpty, cartItems: cartItems };
  }

  function revertCartPageGhostRow(state) {
    if (!state) return;

    /* 'hn-bumped-page' = we incremented an existing cart-page row
       in place instead of inserting a fresh ghost. Restore qty
       attr + chip text + price text + any synthetic bundle chip
       we injected/replaced. Row stays put. */
    if (state.type === 'hn-bumped-page') {
      var row = state.row;
      if (row && row.setAttribute) {
        row.setAttribute('data-hn-variant-qty', String(state.oldQty));
      }
      if (state.chipQty && state.oldChipQtyText != null) {
        state.chipQty.textContent = state.oldChipQtyText;
      }
      if (state.chipValue && state.oldChipValueText != null) {
        state.chipValue.textContent = state.oldChipValueText;
      }
      if (state.priceEl && state.oldPriceText != null) {
        state.priceEl.textContent = state.oldPriceText;
      }
      if (state.bundleChipReplaced) {
        state.bundleChipReplaced.li.innerHTML = state.bundleChipReplaced.oldHTML;
      }
      if (state.bundleChipInserted) {
        var info = state.bundleChipInserted;
        if (info.createdUl && info.ul && info.ul.parentNode) {
          info.ul.parentNode.removeChild(info.ul);
        } else if (info.li && info.li.parentNode) {
          info.li.parentNode.removeChild(info.li);
        }
      }
      return;
    }

    /* Default: ghost-row insertion. Remove the row we inserted
       and restore is-empty state if the cart was empty before. */
    if (state.row && state.row.parentNode) {
      state.row.parentNode.removeChild(state.row);
    }
    if (state.wasEmpty && state.cartItems) {
      state.cartItems.classList.add('is-empty');
    }
  }

  /* /cart page heading "X Items" bump.

     Heading element: <h1 class="hn-cart-page__heading"> with text
     content like "3 Items" or "1 Item". Server-rendered on page
     load; goes stale right after an optimistic add until the
     section swap. Mirrors the drawer heading pattern from
     hn-pdp-cart-optimistic.js. */
  function parseHeadingCount(heading) {
    var match = heading.textContent.trim().match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }

  function bumpCartPageHeading(delta) {
    delta = parseInt(delta, 10) || 0;
    if (!delta) return 0;
    var heading = document.querySelector('.hn-cart-page__heading');
    if (!heading || heading.classList.contains('hn-cart-page__heading--empty')) return 0;

    if (!heading.hasAttribute('data-hn-original-heading')) {
      heading.setAttribute('data-hn-original-heading', heading.textContent.trim());
    }
    var current = parseHeadingCount(heading);
    var next = current + delta;
    if (next <= 0) {
      var original = heading.getAttribute('data-hn-original-heading');
      if (original) heading.textContent = original;
      heading.removeAttribute('data-hn-original-heading');
    } else {
      heading.textContent = next + ' ' + (next === 1 ? 'Item' : 'Items');
    }
    return delta;
  }

  function revertCartPageHeading(delta) {
    delta = parseInt(delta, 10) || 0;
    if (!delta) return;
    bumpCartPageHeading(-delta);
  }

  function parseVariantKeys(member) {
    return (member.getAttribute('data-hn-variant-keys') || '')
      .split(',')
      .map(function (k) { return k.trim(); })
      .filter(Boolean);
  }

  function setMemberBusy(member, busy) {
    member.querySelectorAll('button').forEach(function (b) {
      b.disabled = !!busy;
      if (busy) {
        b.setAttribute('aria-busy', 'true');
      } else {
        b.removeAttribute('aria-busy');
      }
    });
  }

  /* Consolidation logic: when a variant is split across multiple
     line items, set the FIRST line to target_qty and zero the
     rest. Server processes atomically â€” after, the variant lives
     on a single line, simplifying future updates. For removal
     (targetQty === 0), all lines go to 0. */
  function buildConsolidatedUpdates(keys, targetQty) {
    var updates = {};
    if (targetQty <= 0) {
      keys.forEach(function (k) { updates[k] = 0; });
    } else {
      keys.forEach(function (k, i) {
        updates[k] = i === 0 ? targetQty : 0;
      });
    }
    return updates;
  }

  /* Shared POST â†’ /cart/update.js â†’ section swap. Used by all
     three handlers above so the network + reconciliation logic
     lives in one place. onError callback lets each caller clean
     up its own UI state. */
  function postUpdates(updates, onError, onSuccess) {
    markBundleReconcileMutation();

    var body = {
      updates: updates,
      sections: REFRESH_SECTIONS.join(','),
      sections_url: window.location.pathname
    };

    var url = (window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js';

    var request = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (resp) {
        return resp.json().then(function (data) {
          if (!resp.ok) throw new Error(data.description || data.message || 'Cart update failed');
          return data;
        });
      })
      .then(function (data) {
        /* Telemetry callbacks run first so the mutation's own event is
           recorded before any events derived from the section swap
           (e.g. shipbar unlocks) fire. */
        if (typeof onSuccess === 'function') {
          try { onSuccess(data); } catch (e) {}
        }
        applyRenderedSections(data.sections || {}, data);
        notifyCartUpdate(data);
      })
      .catch(function (err) {
        console.error('[hn-cart-drawer] update failed:', err);
        if (typeof onError === 'function') onError(err);
      });
    return trackCartMutationPromise(request);
  }

  function postDiscountCode(code, onDone) {
    markBundleReconcileMutation();

    var body = {
      discount: code,
      sections: REFRESH_SECTIONS.join(','),
      sections_url: window.location.pathname
    };

    var url = (window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js';

    var request = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (resp) {
        return resp.json().then(function (data) {
          if (!resp.ok) throw new Error(data.description || data.message || 'Discount update failed');
          return data;
        });
      })
      .then(function (data) {
        if (typeof onDone === 'function') onDone(null, data);
      })
      .catch(function (err) {
        console.error('[hn-cart-drawer] discount failed:', err);
        if (typeof onDone === 'function') onDone(err);
      });
    return trackCartMutationPromise(request);
  }

  /* M3c discount telemetry (Supabase only). Called from the discount
     form handler at its OWN acceptance decision points, which are
     broader than a code-name match (cart-level discounts apply without
     the code appearing on cartData). */
  function recordDiscountApply(code, accepted) {
    try {
      if (!window.HnTelemetry || typeof window.HnTelemetry.recordEvent !== 'function') return;
      window.HnTelemetry.recordEvent('hi_discount_apply', {
        code: String(code || '').slice(0, 64),
        accepted: accepted === true
      });
    } catch (e) {}
  }

  function cartUpsellSourceLabel(listId) {
    if (LIST_NAME_MAP[listId]) return LIST_NAME_MAP[listId];
    return 'Cart upsell';
  }

  function cartUpsellThresholdContext(card, mode) {
    if (mode === 'below' && card) {
      var remainingCents = parseInt(card.getAttribute('data-hn-upsell-remaining-cents'), 10) || 0;
      if (remainingCents > 0) {
        return 'Customer was ' + formatMoneyCentsLocal(remainingCents) + ' away from free shipping';
      }
      return 'Customer was below free shipping threshold';
    }

    if (mode === 'above') {
      return 'Customer already had free shipping';
    }

    return '';
  }

  function cartUpsellContext(card, listId, mode, quantity) {
    var qty = parseInt(quantity, 10);
    if (!qty || qty < 1) qty = 1;
    var thresholdContext = cartUpsellThresholdContext(card, mode);

    if (listId === 'cart_upsell_quantity_bundle' && card) {
      var currentQty = parseInt(card.getAttribute('data-hn-upsell-current-qty'), 10) || 0;
      var bundleContext = 'Cart bundle bump';
      if (currentQty > 0) {
        bundleContext = 'Cart bundle bump from ' + currentQty + ' to ' + (currentQty + qty) + ' units';
      }
      return thresholdContext ? bundleContext + '. ' + thresholdContext : bundleContext;
    }

    return thresholdContext;
  }

  /* Cart state at add time, read from the server-rendered checkout
     button attributes (drawer or cart-page footer; both render the same
     canonical cart). Values reflect the cart BEFORE this add commits,
     which is exactly the decision context worth recording. */
  function cartStateAtAdd() {
    var el = document.querySelector('[data-hn-cart-value-cents]');
    var value = el ? parseInt(el.getAttribute('data-hn-cart-value-cents'), 10) : NaN;
    var count = el ? parseInt(el.getAttribute('data-hn-cart-item-count'), 10) : NaN;
    return {
      valueCents: isNaN(value) ? null : value,
      itemCount: isNaN(count) ? null : count
    };
  }

  function buildCartUpsellLineProperties(sourceDetail, quantity) {
    var card = sourceDetail && typeof sourceDetail.getAttribute === 'function' ? sourceDetail : null;
    var listId = card ? card.getAttribute('data-hn-upsell-list-id') : (sourceDetail || '');
    var mode = card ? card.getAttribute('data-hn-upsell-mode') : '';
    var properties = {
      _hn_source: cartUpsellSourceLabel(listId)
    };
    var context = cartUpsellContext(card, listId, mode, quantity);
    if (context) properties._hn_upsell_context = context;

    /* Machine-readable companions to the prose above. The prose stays
       for humans reading orders in admin; these make "add rate by gap
       size" a groupby instead of a regex.

       MERGE-SAFETY RULE: Shopify only merges repeat adds into one cart
       line when properties are IDENTICAL, so per-add-varying values may
       only be stamped on line types whose properties already varied per
       add before these numerics existed. That is below-mode gap closers
       and bundle bumps (their prose context changes with every add).
       Above-mode add-ons keep static properties so repeat adds still
       merge into one line; their cart context lives in the event stream
       instead. NOTE: the bump-from quantity deliberately avoids the
       _hn_bundle_ prefix, which the bundle property reconciler wipes
       and rebuilds on every re-render. */
    var isBundleBump = listId === 'cart_upsell_quantity_bundle';
    if (mode === 'below' || isBundleBump) {
      var cartState = cartStateAtAdd();
      if (cartState.valueCents !== null) properties._hn_cart_value_cents = String(cartState.valueCents);
      if (cartState.itemCount !== null) properties._hn_cart_item_count = String(cartState.itemCount);
    }
    if (card && mode === 'below') {
      var remainingCents = parseInt(card.getAttribute('data-hn-upsell-remaining-cents'), 10);
      if (remainingCents > 0) properties._hn_gap_cents = String(remainingCents);
    }
    if (card && isBundleBump) {
      var fromQty = parseInt(card.getAttribute('data-hn-upsell-current-qty'), 10);
      if (fromQty > 0) properties._hn_bump_from_qty = String(fromQty);
    }
    return properties;
  }

  /* POST /cart/add.js for the upsell card. Adds qty 1 of the
     chosen variant, requests the same refresh sections as
     postUpdates so drawer + cart-page + header bubble all
     re-render in one round-trip, then reuses
     applyRenderedSections to swap them in. Callback fires with
     (err) where err is null on success. */
  function addUpsellToCart(variantId, quantity, onDone, sourceDetail) {
    markBundleReconcileMutation();

    // Backward-compat: old call signature was (variantId, onDone).
    // If quantity arrives as a function we treat it as the callback
    // and default qty to 1.
    if (typeof quantity === 'function') {
      onDone = quantity;
      quantity = 1;
    }
    quantity = parseInt(quantity, 10);
    if (!quantity || quantity < 1) quantity = 1;

    /* Add-time provenance for the order line. The reconciler
       maintains the _hn_bundle_* properties but never touches
       _hn_source or _hn_upsell_context. */
    var lineProperties = buildCartUpsellLineProperties(sourceDetail, quantity);

    var body = {
      items: [{ id: variantId, quantity: quantity, properties: lineProperties }],
      sections: REFRESH_SECTIONS.join(','),
      sections_url: window.location.pathname
    };

    var url = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';

    var request = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (resp) {
        return resp.json().then(function (data) {
          if (!resp.ok) throw new Error(data.description || data.message || 'Add to cart failed');
          return data;
        });
      })
      .then(function (data) {
        applyRenderedSections(data.sections || {}, data);
        notifyCartUpdate(data);
        if (typeof onDone === 'function') onDone(null);
      })
      .catch(function (err) {
        console.error('[hn-cart-drawer] upsell add failed:', err);
        if (typeof onDone === 'function') onDone(err);
      });
    return trackCartMutationPromise(request);
  }

  function applyRenderedSections(sections, cartData) {
    /* Mirror Dawn's CartDrawer.renderContents:
       - Replace #CartDrawer innerHTML (NOT the cart-drawer custom
         element's whole innerHTML â€” that destroys the overlay).
       - Toggle is-empty on the <cart-drawer> wrapper using the
         fresh item_count.
       - Rebind the overlay click handler (new node, no listener).
       Section response keys are plain section ids ('cart-drawer',
       'cart-icon-bubble'). The cart-icon-bubble section response
       contains no <cart-drawer> element so DOMParser.querySelector
       for it returns null â€” handle each section separately. */

    var liveDrawer = document.querySelector('cart-drawer');

    if (sections['cart-drawer']) {
      var drawerDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var freshInner = drawerDoc.querySelector('#CartDrawer');
      var liveInner = document.querySelector('#CartDrawer');
      if (freshInner && liveInner) {
        liveInner.innerHTML = freshInner.innerHTML;
      }
      /* Trust the freshly-rendered <cart-drawer>'s `is-empty`
         class â€” /cart/update.js doesn't always include a top-level
         item_count, and the server is the source of truth anyway. */
      var freshDrawer = drawerDoc.querySelector('cart-drawer');
      if (liveDrawer && freshDrawer) {
        liveDrawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }
      var overlay = document.querySelector('#CartDrawer-Overlay');
      if (overlay && liveDrawer && typeof liveDrawer.close === 'function') {
        overlay.addEventListener('click', liveDrawer.close.bind(liveDrawer));
      }
    }

    if (sections['cart-icon-bubble']) {
      var bubbleDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      var freshBubble = bubbleDoc.querySelector('.shopify-section');
      var liveBubble = document.querySelector('#cart-icon-bubble');
      if (freshBubble && liveBubble) {
        liveBubble.innerHTML = freshBubble.innerHTML;
      }
    }

    /* /cart page surfaces. The items column (<cart-items>) is
       reconciled by a full inner swap (see below) so every cart-page
       surface lands canonical now that Dawn's redundant re-render is
       suppressed. The footer (#main-cart-footer) keeps the surgical
       .js-contents swap because its dynamic body is wrapped there per
       Dawn's convention, with an explicit pass for the checkout
       button total that sits outside .js-contents. .is-empty is
       toggled on both wrappers so the empty-cart UI shows/hides
       correctly after the last item is removed. */
    if (sections['main-cart-items']) {
      var itemsDoc = new DOMParser().parseFromString(sections['main-cart-items'], 'text/html');
      var freshCartItemsEl = itemsDoc.querySelector('cart-items');
      var liveCartItemsEl = document.querySelector('cart-items');
      if (freshCartItemsEl && liveCartItemsEl) {
        /* Replace the ENTIRE <cart-items> inner in one pass â€” heading
           ("N Items"), lede, upsell aside, in-list shipbar AND the
           items list. This mirrors how the drawer reconciles its full
           #CartDrawer inner above.

           Previously only #main-cart-items .js-contents (the items
           list) + the upsell aside were swapped, leaving the heading,
           lede, and shipbar to Dawn cart.js's redundant re-render.
           Now that we suppress that re-render (see notifyCartUpdate),
           this swap has to cover every cart-page surface so none goes
           stale until reload. It also fixes a latent gap: when the
           cart empties, the empty-state markup lives OUTSIDE
           .js-contents, so the old partial swap never rendered it.

           Replacing innerHTML (not the element) keeps the <cart-items>
           custom element and its bound handlers intact; our own
           handlers are document-delegated so they survive too. */
        liveCartItemsEl.innerHTML = freshCartItemsEl.innerHTML;
        liveCartItemsEl.classList.toggle('is-empty', freshCartItemsEl.classList.contains('is-empty'));
      }
    }

    if (sections['main-cart-footer']) {
      var footerDoc = new DOMParser().parseFromString(sections['main-cart-footer'], 'text/html');
      var freshFooter = footerDoc.querySelector('#main-cart-footer .js-contents');
      var liveFooter = document.querySelector('#main-cart-footer .js-contents');
      if (freshFooter && liveFooter) {
        liveFooter.innerHTML = freshFooter.innerHTML;
      }
      var freshFooterWrap = footerDoc.querySelector('#main-cart-footer');
      var liveFooterWrap = document.querySelector('#main-cart-footer');
      if (freshFooterWrap && liveFooterWrap) {
        liveFooterWrap.classList.toggle('is-empty', freshFooterWrap.classList.contains('is-empty'));
      }

      /* The checkout button + its inline total sit in the 'buttons'
         block, OUTSIDE #main-cart-footer .js-contents, so the swap
         above never reaches them. Reconcile the button total
         explicitly so it can't drift from the Estimated Total after a
         commit (optimistic bumpCartTotal moved it mid-flight; this
         lands the canonical figure). Scope the live lookup to
         #main-cart-footer so we don't touch the drawer's button â€” that
         one is reconciled by the full #CartDrawer swap above. */
      var freshCheckoutTotal = footerDoc.querySelector('[data-hn-cart-checkout-total]');
      var liveCheckoutTotal = document.querySelector('#main-cart-footer [data-hn-cart-checkout-total]');
      if (freshCheckoutTotal && liveCheckoutTotal) {
        liveCheckoutTotal.textContent = freshCheckoutTotal.textContent;
      }
    }

    /* Re-observe freshly rendered upsell cards. The previous cards
       were destroyed by innerHTML swaps above, so their data-hn-
       upsell-observed flags are gone with them â€” initUpsellTracking
       picks up the new ones idempotently. Decision blob FIRST so
       hnCurrentDecisionKey reflects the new cart state before any card
       observation can fire, then the threshold-crossing check against
       the freshly swapped canonical cart value. */
    syncAllCartQtyControls(document);
    initDecisionTracking();
    initUpsellTracking();
    detectShipbarUnlock();
  }

  /* ============================================================
     GA4 upsell tracking

     Three events, all pushed to window.dataLayer for GTM to consume:
       view_promotion   - card scrolls into view (IntersectionObserver,
                          throttled once per variant per session)
       select_promotion - title or thumb anchor clicked (throttled
                          once per variant per session, anchor still
                          navigates)
       hn_add_to_cart   - successful /cart/add.js for an upsell card

     Card data attributes provide the GA4 item payload â€” see
     snippets/hn-cart-upsell-card.liquid.
     ============================================================ */

  var LIST_NAME_MAP = {
    'cart_upsell_quantity_bundle':  'Cart upsell - bundle bump',
    'cart_upsell_gap_close':        'Cart upsell - free shipping gap closer',
    'cart_upsell_top_product':      'Cart upsell - recommended add-on'
  };

  function buildUpsellItem(card, quantity) {
    var priceCents = parseInt(card.getAttribute('data-hn-upsell-price-cents'), 10) || 0;
    var listId = card.getAttribute('data-hn-upsell-list-id') || 'cart_upsell';
    var variantId = card.getAttribute('data-hn-upsell-variant-id') || '';
    var productId = card.getAttribute('data-hn-upsell-product-id') || '';
    var handle = card.getAttribute('data-hn-upsell-product-handle') || '';
    var sku = card.getAttribute('data-hn-upsell-variant-sku') || '';
    var qty = parseInt(quantity, 10);
    if (!qty || qty < 1) qty = 1;
    return {
      item_id: variantId || sku || handle,
      item_name: card.getAttribute('data-hn-upsell-product-title') || '',
      item_variant: card.getAttribute('data-hn-upsell-variant-title') || '',
      item_brand: card.getAttribute('data-hn-upsell-product-vendor') || '',
      item_category: card.getAttribute('data-hn-upsell-product-type') || '',
      item_list_id: listId,
      item_list_name: LIST_NAME_MAP[listId] || listId,
      item_product_id: productId,
      item_variant_id: variantId,
      item_sku: sku,
      item_handle: handle,
      price: priceCents / 100,
      quantity: qty
    };
  }

  /* The eyebrow actually being displayed on the card (rotating pool,
     UNLOCKS FREE SHIPPING, or the bundle card's per-step copy). Read
     from the DOM at event time so it is always what the shopper saw. */
  function visibleUpsellEyebrow(card) {
    try {
      var eyebrows = card.querySelectorAll('.hn-cart-upsell-card__eyebrow');
      for (var i = 0; i < eyebrows.length; i++) {
        if (eyebrows[i].offsetParent !== null) {
          return (eyebrows[i].textContent || '').trim().slice(0, 64);
        }
      }
    } catch (e) {}
    return '';
  }

  /* Supabase-only enrichment (HARD RULE: GA4 payloads stay frozen, so
     these ride the telemetryParams side channel, never the dataLayer). */
  function upsellTelemetryParams(card, mode) {
    var params = {};
    var priceCents = parseInt(card.getAttribute('data-hn-upsell-price-cents'), 10);
    var compareAtCents = parseInt(card.getAttribute('data-hn-upsell-compare-at-cents'), 10);
    var currentQty = parseInt(card.getAttribute('data-hn-upsell-current-qty'), 10);
    if (!isNaN(priceCents)) params.price_cents = priceCents;
    if (!isNaN(compareAtCents) && compareAtCents > 0) params.compare_at_cents = compareAtCents;
    if (!isNaN(currentQty) && currentQty > 0) params.current_qty = currentQty;
    if (mode === 'below') {
      var gapCents = parseInt(card.getAttribute('data-hn-upsell-remaining-cents'), 10);
      if (gapCents > 0) params.gap_cents = gapCents;
    }
    var eyebrow = visibleUpsellEyebrow(card);
    if (eyebrow) params.eyebrow = eyebrow;

    /* M3d: the OFFER-TIME economics of the card, read from the visible
       price line the shopper is actually looking at. For bundle bumps
       the displayed figure is the true marginal cost of the whole
       stacked deal (tier discount AND any active order code baked in
       server-side); to_tier_pct is the tier the bump would unlock.
       decision_key joins this interaction to the exact cart-state
       decision (composition, order code pct) recorded once per state. */
    try {
      var priceLines = card.querySelectorAll('.hn-cart-upsell-card__price-line');
      for (var p = 0; p < priceLines.length; p++) {
        if (priceLines[p].offsetParent === null) continue;
        var effectiveCents = parseInt(
          priceLines[p].getAttribute('data-hn-qty-price-cents') ||
            priceLines[p].getAttribute('data-hn-display-price-cents'),
          10
        );
        if (!isNaN(effectiveCents)) params.effective_price_cents = effectiveCents;
        var toTierPct = parseInt(priceLines[p].getAttribute('data-hn-qty-tier-pct'), 10);
        if (!isNaN(toTierPct) && toTierPct > 0) params.to_tier_pct = toTierPct;
        break;
      }
    } catch (e) {}
    if (hnCurrentDecisionKey) params.decision_key = hnCurrentDecisionKey;
    params.surface = card.closest('cart-drawer') ? 'drawer' : 'page';
    return params;
  }

  /* M3d: snapshot the offer-time telemetry AT CLICK TIME. The add
     event fires in addUpsellToCart's success callback, AFTER
     applyRenderedSections detached the card (visible-price reads go
     empty, closest('cart-drawer') lies, and hnCurrentDecisionKey has
     already advanced to the post-add cart state). Reading everything
     while the card is live and the pre-add decision key is current is
     the only correct moment. */
  function captureUpsellTelemetry(card) {
    try {
      var aside = card.closest('[data-hn-upsell-mode]');
      var mode = aside ? aside.getAttribute('data-hn-upsell-mode') : '';
      return upsellTelemetryParams(card, mode);
    } catch (e) {
      return null;
    }
  }

  function pushUpsellEvent(eventName, card, quantity, telemetryParamsOverride) {
    if (!card) return;
    var item = buildUpsellItem(card, quantity);
    var aside = card.closest('[data-hn-upsell-mode]');
    var mode = aside ? aside.getAttribute('data-hn-upsell-mode') : '';
    var slot = card.getAttribute('data-hn-upsell-slot') || '';
    var listId = item.item_list_id;
    /* Add events pass a click-time snapshot (see captureUpsellTelemetry);
       live-card events (view/select) compute fresh. */
    var telemetryParams = telemetryParamsOverride || upsellTelemetryParams(card, mode);

    var ecommerce = { items: [item] };
    if (eventName === 'hn_add_to_cart') {
      ecommerce.currency = window.HnAnalytics && typeof window.HnAnalytics.currencyCode === 'function'
        ? window.HnAnalytics.currencyCode()
        : 'USD';
      ecommerce.value = item.price * item.quantity;
    }
    if (eventName === 'view_promotion' || eventName === 'select_promotion') {
      ecommerce.creative_name = 'cart_upsell';
      ecommerce.creative_slot = slot;
      ecommerce.promotion_id = listId;
      ecommerce.promotion_name = LIST_NAME_MAP[listId] || listId;
    }

    var params = {
      cart_source: 'cart_upsell',
      upsell_type: listId,
      upsell_mode: mode,
      upsell_slot: slot
    };

    if (window.HnAnalytics && typeof window.HnAnalytics.pushEcommerce === 'function') {
      window.HnAnalytics.pushEcommerce(eventName, ecommerce, params, { telemetryParams: telemetryParams });
      return;
    }

    if (window.HN_TRACKING_DISABLED === true || window.HnTrackingDisabled === true) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(Object.assign({ event: eventName, ecommerce: ecommerce }, params));

    /* This fallback bypasses hn-analytics.js (its pushEvent hosts the
       telemetry mirror), so mirror explicitly or GA4 sees events the
       first-party pipeline never does. */
    if (window.HnTelemetry && typeof window.HnTelemetry.mirror === 'function') {
      try {
        window.HnTelemetry.mirror(eventName, Object.assign({ ecommerce: ecommerce }, params), telemetryParams);
      } catch (e) {}
    }
  }

  function dedupeOncePerSession(key) {
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
      return true;
    } catch (e) {
      // Private mode / storage disabled â€” always fire (better than never).
      return true;
    }
  }

  /* M3c: cart line mutation telemetry (Supabase only, fail-open).
     from-qty is intentionally omitted: at commit time the optimistic
     DOM already holds the new quantity, and the session's add/decision
     event stream reconstructs the path. Which upsell surface the
     removed line came from is likewise joined from the session's
     earlier hn_add_to_cart events rather than stamped here. */
  function recordCartLineEvent(eventName, keys, toQty, qtyRemoved) {
    try {
      if (!window.HnTelemetry || typeof window.HnTelemetry.recordEvent !== 'function') return;
      var params = { variant_keys: keys, to_qty: toQty };
      if (typeof qtyRemoved === 'number' && qtyRemoved > 0) params.qty_removed = qtyRemoved;
      window.HnTelemetry.recordEvent(eventName, params);
    } catch (e) {}
  }

  /* M3c: free-shipping threshold crossing. Threshold comes from the
     decision blob (initDecisionTracking below); cart value from the
     server-rendered checkout button after each canonical section swap.
     Only the upward crossing fires; overshoot_cents is the incremental
     revenue past the threshold that the gap metric alone hides. */
  var hnShipThresholdCents = null;
  var hnLastCartValueCents = null;

  /* M3d: hash of the CURRENT cart state's decision blob, threaded onto
     every upsell event as decision_key. Set on every blob read (even
     when the decision event itself was session-deduped), so interaction
     events always join to their exact cart context: a naive
     nearest-prior-decision join misattributes when a cart returns to a
     previously seen state. */
  var hnCurrentDecisionKey = '';

  function detectShipbarUnlock() {
    try {
      var el = document.querySelector('[data-hn-cart-value-cents]');
      if (!el) return;
      var value = parseInt(el.getAttribute('data-hn-cart-value-cents'), 10);
      if (isNaN(value)) return;
      var prev = hnLastCartValueCents;
      hnLastCartValueCents = value;
      if (prev === null || hnShipThresholdCents === null || hnShipThresholdCents <= 0) return;
      if (prev < hnShipThresholdCents && value >= hnShipThresholdCents &&
          window.HnTelemetry && typeof window.HnTelemetry.recordEvent === 'function') {
        window.HnTelemetry.recordEvent('hi_shipbar_unlocked', {
          threshold_cents: hnShipThresholdCents,
          overshoot_cents: value - hnShipThresholdCents
        }, { cart_value_cents: value });
      }
    } catch (e) {}
  }

  /* djb2, base36. Only used to build session dedupe keys for decision
     payloads; collisions merely dedupe two distinct cart states. */
  function hashStringForDedupe(str) {
    var hash = 5381;
    for (var i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36);
  }

  /* M3b: read the picker's decision blob(s) rendered by
     snippets/hn-cart-upsell.liquid (drawer AND cart page emit one for
     the same cart state; the content hash dedupes them) and record via
     HnTelemetry.recordEvent ONLY. Never touches the dataLayer: GA4
     must not receive this event. Fail-open throughout. */
  function initDecisionTracking() {
    var blobs = document.querySelectorAll('script[data-hn-upsell-decision]:not([data-hn-decision-read])');
    for (var i = 0; i < blobs.length; i++) {
      blobs[i].setAttribute('data-hn-decision-read', '1');
      try {
        /* Telemetry presence is checked BEFORE the dedupe key is
           burned: if hn-telemetry.js failed to load on this pageview,
           the cart state stays recordable on a later one. */
        if (!window.HnTelemetry || typeof window.HnTelemetry.recordEvent !== 'function') continue;
        var raw = blobs[i].textContent;
        var payload = JSON.parse(raw);
        if (typeof payload.threshold_cents === 'number') hnShipThresholdCents = payload.threshold_cents;
        var decisionKey = hashStringForDedupe(raw);
        hnCurrentDecisionKey = decisionKey;
        if (!dedupeOncePerSession('hi_upsell_decision:' + decisionKey)) continue;
        payload.decision_key = decisionKey;
        window.HnTelemetry.recordEvent('hi_upsell_decision', payload, {
          cart_value_cents: typeof payload.cart_value_cents === 'number' ? payload.cart_value_cents : undefined,
          cart_item_count: typeof payload.item_count === 'number' ? payload.item_count : undefined
        });
      } catch (e) {}
    }
  }

  function fireViewPromotion(card) {
    var key = 'hn_upsell_view:' + card.getAttribute('data-hn-upsell-list-id') + ':' + card.getAttribute('data-hn-upsell-variant-id');
    if (!dedupeOncePerSession(key)) return;
    pushUpsellEvent('view_promotion', card, 1);
  }

  function fireSelectPromotion(card) {
    var key = 'hn_upsell_select:' + card.getAttribute('data-hn-upsell-list-id') + ':' + card.getAttribute('data-hn-upsell-variant-id');
    if (!dedupeOncePerSession(key)) return;
    pushUpsellEvent('select_promotion', card, 1);
  }

  function fireAddToCart(card, quantity, telemetryParamsSnapshot) {
    /* No dedupe â€” every successful add is an event. */
    pushUpsellEvent('hn_add_to_cart', card, quantity, telemetryParamsSnapshot);
  }

  var upsellObserver = null;

  function initUpsellTracking() {
    var cards = document.querySelectorAll('[data-hn-upsell-card]:not([data-hn-upsell-observed])');
    if (!cards.length) return;

    if (!('IntersectionObserver' in window)) {
      // Fallback: fire view_promotion immediately for all cards.
      cards.forEach(function (c) {
        c.setAttribute('data-hn-upsell-observed', '1');
        fireViewPromotion(c);
      });
      return;
    }

    if (!upsellObserver) {
      upsellObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            fireViewPromotion(entry.target);
            upsellObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });
    }

    cards.forEach(function (c) {
      c.setAttribute('data-hn-upsell-observed', '1');
      upsellObserver.observe(c);
    });
  }

  /* Initial wire-up on DOM ready. After every section swap (cart
     update / upsell add), applyRenderedSections calls this again
     to re-observe freshly rendered cards. */
  function initCartEnhancements() {
    syncAllCartQtyControls(document);
    initDecisionTracking();
    initUpsellTracking();
    detectShipbarUnlock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartEnhancements);
  } else {
    initCartEnhancements();
  }

  function notifyCartUpdate(cart) {
    /* Broadcast Dawn's pub/sub event so other listeners (price-per-
       item, sticky ATC bar, analytics, etc.) refresh in lockstep, and
       a plain CustomEvent for any non-Dawn subscribers.

       source: 'cart-items' is deliberate. Dawn's CartItems /
       CartDrawerItems elements (cart.js) skip their own re-render when
       the event source is 'cart-items' (their guard against self-
       triggering). We've REPLACED Dawn's qty/remove handling with our
       own /cart/update.js path and already swap every section in
       applyRenderedSections above, so we ARE the cart-items updater
       now â€” letting Dawn ALSO re-fetch and re-render produced a double
       render plus a stale-overwrite race (its fetch is a separate
       round-trip that can land out of order). Claiming the 'cart-items'
       source suppresses that redundant work; every other subscriber
       (none of which special-cases this source against us) still
       reacts normally. */
    /* PUB_SUB_EVENTS is a bare const in constants.js (a classic script),
       so it never lands on window; only the bare reference resolves. */
    if (typeof window.publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      window.publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: cart
      });
    }
    document.dispatchEvent(new CustomEvent('hn:cart:updated', {
      detail: { cart: cart }
    }));
    if (!bundleReconcileFromSelf) {
      scheduleBundlePropertyReconcile('notify');
    }
  }

  function markBundleReconcileMutation() {
    bundleReconcileMutationGen += 1;
    return bundleReconcileMutationGen;
  }

  function isBundleReconcileDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function bundleReconcileJsonUrl(routeKey, fallback) {
    var url = window.routes && window.routes[routeKey] ? window.routes[routeKey] : fallback;
    return /\.js(?:\?|$)/.test(url) ? url : url + '.js';
  }

  function scheduleBundlePropertyReconcile(reason) {
    if (isBundleReconcileDesignMode()) return;
    if (bundleReconcileTimer) clearTimeout(bundleReconcileTimer);
    bundleReconcileTimer = setTimeout(function () {
      bundleReconcileTimer = null;
      runBundlePropertyReconcile(reason);
    }, BUNDLE_RECONCILE_DEBOUNCE_MS);
  }

  function runBundlePropertyReconcile(reason) {
    if (isBundleReconcileDesignMode()) return Promise.resolve();
    if (qtyPendingCommits.size) return Promise.resolve();
    if (bundleReconcileInFlight) {
      scheduleBundlePropertyReconcile(reason || 'in-flight');
      return bundleReconcilePromise || Promise.resolve();
    }

    var startGen = bundleReconcileMutationGen;
    bundleReconcileInFlight = true;

    bundleReconcilePromise = fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (resp) {
        return resp.json().then(function (data) {
          if (!resp.ok) throw new Error(data.description || data.message || 'Cart fetch failed');
          return data;
        });
      })
      .then(function (cart) {
        if (bundleReconcileMutationGen !== startGen || qtyPendingCommits.size) {
          return { cart: cart, applied: false, stale: true };
        }
        return reconcileBundlePropertiesLoop(cart, startGen, 0, false);
      })
      .then(function (result) {
        if (!result || result.stale || !result.applied || !result.cart) return;
        if (bundleReconcileMutationGen !== startGen || qtyPendingCommits.size) return;

        bundleReconcileFromSelf = true;
        try {
          applyRenderedSections(result.cart.sections || {}, result.cart);
          notifyCartUpdate(result.cart);
        } finally {
          bundleReconcileFromSelf = false;
        }
      })
      .catch(function (err) {
        console.warn('[hn-cart-drawer] bundle property reconcile failed:', err);
      })
      .finally(function () {
        bundleReconcileInFlight = false;
        bundleReconcilePromise = null;
      });
    return bundleReconcilePromise;
  }

  function flushBundlePropertyReconcileForCheckout() {
    if (bundleReconcileTimer) {
      clearTimeout(bundleReconcileTimer);
      bundleReconcileTimer = null;
    }

    if (bundleReconcileInFlight && bundleReconcilePromise) {
      return bundleReconcilePromise.then(function () {
        if (bundleReconcileTimer) {
          clearTimeout(bundleReconcileTimer);
          bundleReconcileTimer = null;
        }
        return runBundlePropertyReconcile('checkout-after-flight');
      });
    }

    return runBundlePropertyReconcile('checkout');
  }

  function flushCartStateBeforeCheckout(target) {
    var commitPromises = commitPendingQtyIfOutside(target);
    return Promise.all(commitPromises || [])
      .then(waitForCartMutations)
      .then(flushBundlePropertyReconcileForCheckout)
      .then(waitForCartMutations);
  }

  function checkoutSubmitterForEvent(event) {
    if (event && event.submitter) return event.submitter;
    var active = document.activeElement;
    if (active && active.getAttribute && active.getAttribute('name') === 'checkout') return active;
    return null;
  }

  function isCheckoutSubmit(form, submitter) {
    if (!form) return false;
    if (submitter && submitter.getAttribute && submitter.getAttribute('name') === 'checkout') return true;
    return form.id === 'cart' || form.id === 'CartDrawer-Form';
  }

  function liveElementById(element) {
    if (element && element.id) return document.getElementById(element.id) || element;
    return element;
  }

  function checkoutButtonForForm(form) {
    if (!form) return null;
    var buttons = document.querySelectorAll('[name="checkout"]');
    for (var i = 0; i < buttons.length; i += 1) {
      if (buttons[i].form === form) return buttons[i];
    }
    return null;
  }

  function submitCheckoutForm(form, submitter) {
    var liveSubmitter = liveElementById(submitter);
    var liveForm = liveSubmitter && liveSubmitter.form ? liveSubmitter.form : liveElementById(form);
    if (!liveForm) return;

    if (!liveSubmitter || liveSubmitter.form !== liveForm) {
      liveSubmitter = checkoutButtonForForm(liveForm);
    }

    /* Re-stamp the telemetry session input NOW: the pre-checkout flush
       may have re-rendered the drawer (recreating the input empty), and
       the form.submit() fallback below fires no submit event, so the
       just-in-time stamp listener in hn-telemetry.js never sees it.
       requestSubmit is covered too; stamping twice is harmless. */
    if (window.HnTelemetry && typeof window.HnTelemetry.stampSessionInputs === 'function') {
      try {
        window.HnTelemetry.stampSessionInputs(liveForm);
      } catch (e) {}
    }

    if (typeof liveForm.requestSubmit === 'function' && liveSubmitter && liveSubmitter.form === liveForm) {
      liveForm.requestSubmit(liveSubmitter);
      return;
    }

    if (!liveForm.querySelector('input[name="checkout"][data-hn-checkout-hidden]')) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = 'Checkout';
      hidden.setAttribute('data-hn-checkout-hidden', 'true');
      liveForm.appendChild(hidden);
    }
    liveForm.submit();
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var submitter = checkoutSubmitterForEvent(event);
    if (!isCheckoutSubmit(form, submitter)) return;

    if (checkoutSubmitUnlocked) {
      checkoutSubmitUnlocked = false;
      return;
    }

    event.preventDefault();
    if (checkoutFlushInProgress) return;
    checkoutFlushInProgress = true;

    if (submitter && submitter.setAttribute) {
      submitter.setAttribute('aria-disabled', 'true');
      submitter.classList.add('loading');
    }

    flushCartStateBeforeCheckout(submitter || form)
      .catch(function (err) {
        console.warn('[hn-cart-drawer] checkout attribution flush failed:', err);
      })
      .then(function () {
        checkoutFlushInProgress = false;
        checkoutSubmitUnlocked = true;
        submitCheckoutForm(form, submitter);
      });
  }, true);

  function reconcileBundlePropertiesLoop(cart, startGen, iteration, applied) {
    if (bundleReconcileMutationGen !== startGen || qtyPendingCommits.size) {
      return Promise.resolve({ cart: cart, applied: applied, stale: true });
    }

    if (iteration >= BUNDLE_RECONCILE_MAX_ITERATIONS) {
      console.warn('[hn-cart-drawer] bundle property reconcile hit iteration cap');
      return Promise.resolve({ cart: cart, applied: applied });
    }

    var change = findNextBundlePropertyChange(cart);
    if (!change) {
      bundleReconcilePoison = '';
      return Promise.resolve({ cart: cart, applied: applied });
    }

    if (change.fingerprint === bundleReconcilePoison) {
      return Promise.resolve({ cart: cart, applied: applied });
    }

    return postBundlePropertyChange(change)
      .then(function (nextCart) {
        if (bundleReconcilePoison === change.fingerprint) bundleReconcilePoison = '';
        return reconcileBundlePropertiesLoop(nextCart, startGen, iteration + 1, true);
      })
      .catch(function (err) {
        bundleReconcilePoison = change.fingerprint;
        throw err;
      });
  }

  function findNextBundlePropertyChange(cart) {
    var groups = buildBundleReconcileGroups(cart);
    var productIds = Object.keys(groups);

    for (var i = 0; i < productIds.length; i += 1) {
      var productId = productIds[i];
      var group = groups[productId];
      if (!group || !group.items.length) continue;

      group.pct = pageBundleTierPctFor(group.totalQty);
      group.savingsCents = bundleGroupSavingsCents(group.items);

      if (group.pct > 0 && group.savingsCents <= 0 && !bundleReconcileMissingAllocationWarnings[productId]) {
        console.warn('[hn-cart-drawer] bundle tier present without Shopify allocation; stripping bundle properties for product ' + productId);
        bundleReconcileMissingAllocationWarnings[productId] = true;
      }

      if (group.pct > 0 && group.savingsCents > 0 && !group.bundleId) {
        group.bundleId = 'b_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      }

      for (var j = 0; j < group.items.length; j += 1) {
        var item = group.items[j];
        var desired = desiredBundlePropertiesForItem(item, group);
        if (!bundlePropertiesEqual(item.properties || {}, desired)) {
          return {
            key: item.key,
            quantity: item.quantity,
            properties: desired,
            fingerprint: bundleChangeFingerprint(item, desired)
          };
        }
      }
    }

    return null;
  }

  function buildBundleReconcileGroups(cart) {
    var items = (cart && cart.items) || [];
    var eligibleProductIds = collectBundleEligibleProductIds(items);
    var groups = {};

    items.forEach(function (item) {
      if (!item || !item.product_id) return;
      var productId = String(item.product_id);
      if (!eligibleProductIds[productId]) return;

      if (!groups[productId]) {
        groups[productId] = {
          items: [],
          totalQty: 0,
          bundleId: ''
        };
      }

      var props = item.properties || {};
      if (!groups[productId].bundleId && props._hn_bundle_id) {
        groups[productId].bundleId = String(props._hn_bundle_id);
      }

      groups[productId].items.push(item);
      groups[productId].totalQty += parseInt(item.quantity, 10) || 0;
    });

    return groups;
  }

  function collectBundleEligibleProductIds(items) {
    var eligible = {};
    var variantToProduct = {};

    items.forEach(function (item) {
      if (!item) return;
      if (item.variant_id && item.product_id) {
        variantToProduct[String(item.variant_id)] = String(item.product_id);
      }
      if (item.product_id && hasBundleProperty(item.properties)) {
        eligible[String(item.product_id)] = true;
      }
    });

    document.querySelectorAll('[data-hn-bundle-product-id]').forEach(function (el) {
      var id = el.getAttribute('data-hn-bundle-product-id');
      if (id) eligible[String(id)] = true;
    });

    document.querySelectorAll('[data-hn-bundle-eligible="true"], .cart-item--bundle-member').forEach(function (row) {
      if (row.hasAttribute('data-hn-product-id')) {
        eligible[String(row.getAttribute('data-hn-product-id'))] = true;
      }

      row.querySelectorAll('[data-hn-product-id]').forEach(function (el) {
        var id = el.getAttribute('data-hn-product-id');
        if (id) eligible[String(id)] = true;
      });

      var variantId = row.getAttribute('data-hn-variant-id');
      if (variantId && variantToProduct[String(variantId)]) {
        eligible[variantToProduct[String(variantId)]] = true;
      }
    });

    return eligible;
  }

  function hasBundleProperty(properties) {
    if (!properties) return false;
    for (var key in properties) {
      if (Object.prototype.hasOwnProperty.call(properties, key) && key.indexOf('_hn_bundle_') === 0) {
        return true;
      }
    }
    return false;
  }

  function desiredBundlePropertiesForItem(item, group) {
    var next = stripBundleProperties(item.properties || {});

    if (group.pct > 0 && group.savingsCents > 0) {
      next._hn_bundle_id = group.bundleId;
      next._hn_bundle_label = group.totalQty + ' Pack';
      next._hn_bundle_qty = String(group.totalQty);
      next._hn_bundle_discount = group.pct + '%';
      next._hn_bundle_savings = formatMoneyCentsLocal(group.savingsCents);
    }

    return next;
  }

  function stripBundleProperties(properties) {
    var next = {};
    for (var key in properties) {
      if (!Object.prototype.hasOwnProperty.call(properties, key)) continue;
      if (key.indexOf('_hn_bundle_') === 0) continue;
      next[key] = properties[key];
    }
    return next;
  }

  function bundlePropertiesEqual(a, b) {
    var aKeys = Object.keys(a || {});
    var bKeys = Object.keys(b || {});
    if (aKeys.length !== bKeys.length) return false;

    for (var i = 0; i < aKeys.length; i += 1) {
      var key = aKeys[i];
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (String(a[key]) !== String(b[key])) return false;
    }

    return true;
  }

  function bundleChangeFingerprint(item, desired) {
    return [
      item && item.key,
      item && item.quantity,
      JSON.stringify(item && item.properties ? item.properties : {}),
      JSON.stringify(desired || {})
    ].join('|');
  }

  function bundleGroupSavingsCents(items) {
    var total = 0;
    items.forEach(function (item) {
      var allocations = (item && item.line_level_discount_allocations) || [];
      allocations.forEach(function (allocation) {
        var app = allocation && allocation.discount_application;
        var title = app && app.title;
        if (!bundleDiscountTitleMatches(title)) return;
        total += parseInt(allocation.amount, 10) || 0;
      });
    });
    return total;
  }

  function bundleDiscountTitleMatches(title) {
    return /^\d+\s+Pack\s+\d+\s*%\s*Off$/i.test(String(title || '').trim());
  }

  function postBundlePropertyChange(change) {
    var body = {
      id: change.key,
      quantity: change.quantity,
      properties: change.properties,
      sections: REFRESH_SECTIONS.join(','),
      sections_url: window.location.pathname
    };

    return fetch(bundleReconcileJsonUrl('cart_change_url', '/cart/change'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (resp) {
        return resp.json().then(function (data) {
          if (!resp.ok) throw new Error(data.description || data.message || 'Cart property change failed');
          return data;
        });
      });
  }

  function getBundleReconcilePubSubEvents() {
    if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS) return PUB_SUB_EVENTS;
    if (window.PUB_SUB_EVENTS) return window.PUB_SUB_EVENTS;
    return null;
  }

  function bindBundleReconcilePubSub() {
    if (bindBundleReconcilePubSub.__done) return true;
    var events = getBundleReconcilePubSubEvents();
    if (typeof window.subscribe !== 'function' || !events || !events.cartUpdate) {
      return false;
    }

    window.subscribe(events.cartUpdate, function (event) {
      var source = event && event.source;
      if (source === 'cart-items') return;
      markBundleReconcileMutation();
      scheduleBundlePropertyReconcile(source || 'pubsub');
    });

    bindBundleReconcilePubSub.__done = true;
    return true;
  }

  function startBundleReconcilePubSubBindAttempts() {
    if (bindBundleReconcilePubSub()) return;
    var tries = 0;
    var interval = setInterval(function () {
      if (bindBundleReconcilePubSub() || ++tries > 50) {
        clearInterval(interval);
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startBundleReconcilePubSubBindAttempts);
  } else {
    startBundleReconcilePubSubBindAttempts();
  }

  /* ---------- Public footer API for the PDP add paths ----------
     The PDP ATC layer (hn-pdp-cart-optimistic.js / hn-pdp-main.js)
     lives in a separate IIFE and already paints the ghost row,
     count badge, and shipping bar on the click frame â€” but it can't
     reach the Estimated Total + Total Savings updaters that live in
     here. Expose them so a PDP add updates the footer in lockstep,
     reusing this file's penny-accurate math (Shopify floors, retail-
     based savings) instead of a divergent copy. The cart drawer is
     global, so this runs on every page including product pages.

     applyLineAddToFooter takes the line's already-final delta (its
     contribution to cart.total_price, AFTER every discount incl. the
     order code, which the caller computes per line â€” Shopify floors the
     order-code DISCOUNT per line) + its retail delta (MAX(compare_at,
     price) Ã— qty). It bumps the Estimated Total by the final delta and
     Total Savings by (retail âˆ’ final). Returns a handle the caller
     reverts on a failed add; the success-path section swap reconciles
     either way. */
  window.HnCart = window.HnCart || {};
  window.HnCart.applyLineAddToFooter = function (finalDeltaCents, retailDeltaCents) {
    var finalDelta = parseInt(finalDeltaCents, 10) || 0;
    var retailDelta = parseInt(retailDeltaCents, 10) || 0;
    var totalRevert = finalDelta ? bumpCartTotal(finalDelta) : null;
    var savingsDelta = retailDelta - finalDelta;
    var savingsRevert = savingsDelta ? bumpCartSavings(savingsDelta) : null;
    return { totalRevert: totalRevert, savingsRevert: savingsRevert };
  };
  window.HnCart.revertLineAddToFooter = function (handle) {
    if (!handle) return;
    revertCartTotal(handle.totalRevert);
    revertCartSavings(handle.savingsRevert);
  };
})();
